/**
 * Project Scripts
 *
 * @package    Göhler Tool
 * @author     pixit-webdesign
 */

import { Project_Materials_Controller } from '@app/controllers/materials-controller'
import { Project_Manage_Assembly_Presenter } from '@app/controllers/assembly-controller'

import { Toast } from '@app/utils/toast'

import type { GT_Project } from '../types/projects'
import type { GT_Material } from '../types/materials'
import { Project_Invoices_Controller } from '@app/controllers/invoices-controller'
import { GT_Invoice } from '../types/invoices'
import { Project_Manage_Status_Workers_Presenter } from '@app/controllers/workers-controller'
import { Project_Measurement_Controller, Project_Measurement_Generator } from '@app/controllers/measurement-controller'
import { Tasks_Controller } from '@app/controllers/tasks-controller'
import { Deviation_Chart } from '@app/vis/deviation-chart';
import { mount_deviation_chart } from '@app/vis/mount-deviation-chart';
import {Project_Times_Controller} from "@app/controllers/times-controller";
import {GT_Time} from "../types/times";
import {Goehler_Tool_REST_Client} from "@app/utils/rest-client";
import {Api_Error, handle_api_error} from "@app/utils/api-error";
import {Project_Status_Controller} from "@app/controllers/status-controller";

jQuery(
	() => {
        if ( typeof project === 'object' ) {
            project.ready()
        }
	}
)

export class Project {
	post_id: number
	extra_work
    remaining_work
	support
	times
	user_notes
	dates
    tabs

    meta: Map<string, object>
    loaded_data: object

    invoice_controller: Project_Invoices_Controller
    materials_controller: Project_Materials_Controller
    assembly_presenter: Project_Manage_Assembly_Presenter
    status_workers_presenter: Project_Manage_Status_Workers_Presenter
    measurement_controller: Project_Measurement_Controller
    measurement_generator?: Project_Measurement_Generator
    tasks_controller: Tasks_Controller
    times_controller: Project_Times_Controller
    status_controller: Project_Status_Controller

    deviation_chart: Deviation_Chart | null = null

    rest_client: Goehler_Tool_REST_Client

	constructor( data: GT_Project ) {
		this.meta = new Map()
		this.loaded_data = data
		this.post_id = data.post_id

		this.init_meta( 'post-id', data.post_id )
		this.init_meta( 'project-title', data.title )
		this.init_meta( 'project-status', data.status )
		this.init_meta( 'project-substatus', data.substatus )
		this.init_meta( 'project-assembly', data.assembly )
		this.init_meta( 'project-price', data.price )
		this.init_meta( 'project-customer-street', data.customer?.street )
		this.init_meta( 'project-customer-zip-location', data.customer?.zip_location )
		this.init_meta( 'project-customer-tel', data.customer?.tel )
		this.init_meta( 'project-customer-email', data.customer?.email )
		this.init_meta( 'project-global-notes', data.global_notes )
		this.init_meta( 'project-user-notes', data.user_notes )
		this.init_meta( 'project-planning-estimates', data.planning_estimates )
		this.init_meta( 'project-planning-offer-estimates', data.planning_offer_estimates )
		this.init_meta( 'project-properties', data.properties )
		this.init_meta( 'project-measurement', data.measurement )
		this.init_meta( 'project-construction-plans', data.construction_plans )
		this.init_meta( 'project-status-workers', data.status_workers )
		this.init_meta( 'project-deadlines', data.deadlines )
		this.init_meta( 'project-storage-yards', data.storage_yards )
		this.init_meta( 'project-approval-dates', data.approval_dates )
		this.init_meta( 'project-start-dates', data.start_dates )
		this.init_meta( 'project-surface-treatment', data.surface_treatment )
		this.init_meta( 'project-total-invoiced-amount', data.total_invoiced_amount )
        this.init_meta( 'project-measurement-id', data.measurement_id )
        this.init_meta( 'project-times-net', data.times_net )
        this.init_meta( 'project-status-reasons', data.status_reasons )
        this.init_meta( 'project-data', data.data )
        this.init_meta( 'project-return-reason', data.return_reason )

        this.materials_controller = new Project_Materials_Controller( materials )
        this.assembly_presenter = new Project_Manage_Assembly_Presenter()
        this.invoice_controller = new Project_Invoices_Controller( invoices )
        this.status_workers_presenter = new Project_Manage_Status_Workers_Presenter()
        this.measurement_controller = new Project_Measurement_Controller( measurement )
        this.times_controller = new Project_Times_Controller()
        this.status_controller = new Project_Status_Controller()

        if ( ! measurement ) {
            this.measurement_generator = new Project_Measurement_Generator()
        }

        this.set_extra_work( extra_work )
		this.set_support( support )
		this.set_remaining_work( remaining_work )
		this.set_times( times )
		this.set_project_documents( project_documents )
		this.set_transfer_documents( transfer_documents )
		this.set_user_notes( user_notes )
		this.set_dates( dates )

        this.rest_client = new Goehler_Tool_REST_Client()
	}

    ready() {
        console.debug( 'ready: Project' )
        this.init_events()
        this.init_actions()
        this.init_calendars()
        this.init_progress()
        this.init_dropdowns()
        this.init_checkboxes()
        this.init_forms()
        this.init_popups()
        this.init_tabs()
        this.init_nags()

        // this.materials_controller.ready()
        this.assembly_presenter.ready()
        this.status_workers_presenter.ready()
        this.times_controller.ready()
        this.status_controller.ready()

        this.tasks_controller = Tasks_Controller.init()
        this.deviation_chart = mount_deviation_chart(
            this.get_meta_value( 'project-approval-dates' ),
            this.get_meta_value( 'project-start-dates' ),
        )
    }

    public set materials( data: GT_Material[] ) {
        this.materials_controller.set_materials( data )
    }

    public get materials(): GT_Material[] {
        return this.materials_controller.materials
    }

    public set materials_filter( filter: string ) {
        this.materials_controller.set_filter( filter )
    }

    public get materials_filter(): string {
        return this.materials_controller.filter
    }

    public set invoices( data: GT_Invoice[] ) {
        this.invoice_controller.set_invoices( data )
    }

    public get invoices(): GT_Invoice[] {
        return this.invoice_controller.invoices
    }

	init_events() {
        const toggle_tasks = document.querySelectorAll( '[data-action="toggle-task"]' )

		for ( const task of toggle_tasks ) {
			task.addEventListener( 'click', ( e ) => project.toggle_task( task, e ) )
		}

		jQuery( '.action-container[data-post-type="extra-work"] .item-list' ).on(
			'click',
			( e ) => {
				const target = e.target.closest( '.list-item' )

				if ( target == null ) {
					return
				}

				if ( 'debug' in e.target.dataset ) {
					return
				}

				if ( e.target.nodeName === 'IMG' ) {
					e.preventDefault()
					project.init_modal_view_extra_work_image( e.target )
					return
				}

				if ( e.target.dataset.action === 'update-extra-work-thumbnail' ) {
					project.init_modal_update_extra_work_thumbnail( target )
					return
				}

				if ( e.target.dataset.action === 'update-extra-work-images' ) {
					project.init_modal_update_extra_work_images( target )
					return
				}

				if ( e.target.dataset.action === 'update-extra-work-item' ) {
					project.init_modal_update_extra_work( target )
					return
				}

				target.classList.toggle( 'collapsed' )
			}
		)

		jQuery( '.action-container[data-post-type="remaining-work"] .item-list' ).on(
			'click',
			( e ) => {
				const target = e.target.closest( '.list-item' )

				if ( target == null ) {
					return
				}

				if ( 'debug' in e.target.dataset ) {
					return
				}

				if ( e.target.nodeName === 'IMG' ) {
					e.preventDefault()
					project.init_modal_view_remaining_work_image( e.target )
					return
				}

				if ( e.target.dataset.action === 'update-remaining-work-thumbnail' ) {
					project.init_modal_update_remaining_work_thumbnail( target )
					return
				}

				if ( e.target.dataset.action === 'update-remaining-work-images' ) {
					project.init_modal_update_remaining_work_images( target )
					return
				}

				if ( e.target.dataset.action === 'update-remaining-work-item' ) {
					project.init_modal_update_remaining_work( target )
					return
				}

				if ( e.target.dataset.action === 'approve-remaining-work-item' ) {
					project.init_modal_approve_remaining_work( target )
					return
				}

				if ( e.target.dataset.action === 'show-remaining-work-status-log' ) {
					project.init_modal_remaining_work_status_log( target )
					return
				}

				target.classList.toggle( 'collapsed' )
			}
		)

		jQuery( '.action-container[data-post-type="attachment"][data-category="project-document"] .item-list' ).on(
			'click',
			( e ) => {
				const target = e.target.closest( '.list-item' )

				if ( target == null ) {
					return
				}

				if ( 'debug' in e.target.dataset ) {
					return
				}

				if ( e.target.dataset.action === 'update-project-document' ) {
					project.init_modal_update_project_document( target )
					return
				}

				target.classList.toggle( 'collapsed' )
			}
		)

		jQuery( '.action-container[data-post-type="attachment"][data-category="transfer-document"] .item-list' ).on(
			'click',
			( e ) => {
				const target = e.target.closest( '.list-item' )

				if ( target == null ) {
					return
				}

				if ( 'debug' in e.target.dataset ) {
					return
				}

				if ( e.target.dataset.action === 'update-transfer-document' ) {
					project.init_modal_update_transfer_document( target )
					return
				}
			}
		)

		const transfer_documents = document.querySelector( '.transfer-documents-list' )
		if ( transfer_documents ) {
			transfer_documents.addEventListener(
				'click',
				( e ) => {
					console.log( 'clicked', e.target )

					if ( e.target.dataset.action === 'remove-transfer-document-input' ) {
						e.preventDefault()
						console.log( 'delete transfer document input' )
						project.delete_transfer_document_input( e.target )
					}

					if ( e.target.dataset.action === 'add-transfer-document-input' ) {
						e.preventDefault()
						console.log( 'add project document input' )
						project.create_add_transfer_document_input()
					}
				}
			)
		}

		jQuery( '#form-edit-remaining-work-images .current-images' ).on(
			'click',
			( e ) => {
				const target = e.target.closest( '.item-image' )

				if ( target == null ) {
					return
				}

				if ( e.target.dataset.action === 'delete-current-image' ) {
					project.delete_remaining_work_image( target )
					return
				}
			}
		)

		jQuery( '#form-edit-extra-work-images .current-images' ).on(
			'click',
			( e ) => {
				const target = e.target.closest( '.item-image' )

				if ( target == null ) {
					return
				}

				if ( e.target.dataset.action === 'delete-current-image' ) {
					project.delete_extra_work_image( target )
					return
				}
			}
		)
	}

	init_actions() {
		jQuery( '[data-action="update-measurement-date"]' ).on( 'click', () => project.init_modal_update_measurement_date() )
		jQuery( '[data-action="update-construction-plans-date"]' ).on( 'click', () => project.init_modal_update_construction_plans_date() )

		jQuery( '[data-action="delete-project"]' ).on( 'click', () => project.delete_confirmation() )

		jQuery( '[data-action="prioritize-project"]' ).on( 'click', () => project.add_priority() )
		jQuery( '[data-action="deprioritize-project"]' ).on( 'click', () => project.remove_priority() )

		jQuery( '[data-action="pause-project"]' ).on( 'click', () => project.pause_confirmation() )
		jQuery( '[data-action="unpause-project"]' ).on( 'click', () => project.unpause_confirmation() )

		// jQuery( '[data-action="stop-time"]' ).on( 'click', () => project.stop_time_confirmation() )

		jQuery( '[data-action="add-extra-work"]' ).on( 'click', () => project.init_modal_add_extra_work() )
		jQuery( '[data-action="delete-extra-work"]' ).on( 'click', ( e ) => project.delete_extra_work_confirmation( e ) )
		jQuery( '[data-action="delete-extra-work-thumbnail"]' ).on( 'click', ( e ) => project.delete_extra_work_thumbnail_confirmation( e ) )
		jQuery( '[data-action="delete-extra-work-images"]' ).on( 'click', ( e ) => project.delete_extra_work_images( e ) )

		jQuery( '[data-action="add-remaining-work"]' ).on( 'click', () => project.init_modal_add_remaining_work() )
		jQuery( '[data-action="complete-remaining-work"]' ).on( 'click', ( e ) => project.complete_remaining_work_confirmation( e ) )
		jQuery( '[data-action="delete-remaining-work"]' ).on( 'click', ( e ) => project.delete_remaining_work_confirmation( e ) )
		jQuery( '[data-action="delete-remaining-work-thumbnail"]' ).on( 'click', ( e ) => project.delete_remaining_work_thumbnail_confirmation( e ) )
		jQuery( '[data-action="delete-remaining-work-images"]' ).on( 'click', ( e ) => project.delete_remaining_work_images( e ) )

		jQuery( '[data-action="expand-extra-work"]' ).on( 'click', () => project.expand_extra_work() )
		jQuery( '[data-action="collapse-extra-work"]' ).on( 'click', () => project.collapse_extra_work() )

		jQuery( '[data-action="expand-remaining-work"]' ).on( 'click', () => project.expand_remaining_work() )
		jQuery( '[data-action="collapse-remaining-work"]' ).on( 'click', () => project.collapse_remaining_work() )

		jQuery( '[data-action="add-project-documents"]' ).on( 'click', () => project.init_modal_add_project_documents() )
		jQuery( '[data-action="delete-project-document"]' ).on( 'click', ( e ) => project.delete_project_document_confirmation( e ) )

		jQuery( '[data-action="add-transfer-documents"]' ).on( 'click', () => project.init_modal_add_transfer_documents() )
		jQuery( '[data-action="delete-transfer-document"]' ).on( 'click', ( e ) => project.delete_transfer_document_confirmation( e ) )

		jQuery( '[data-action="update-project-deadline"]' ).on( 'click', () => project.init_modal_update_deadline() )
		jQuery( '[data-action="reset-project-deadline"]' ).on( 'click', ( e ) => project.reset_deadline( e ) )
		jQuery( '[data-action="delete-project-deadline"]' ).on( 'click', ( e ) => project.delete_deadline_confirmation( e ) )

		jQuery( '[data-action="update-storage-yard"]' ).on( 'click', () => project.init_modal_update_storage_yard() )

		jQuery( '[data-action="update-project-user-notes"]' ).on( 'click', () => project.init_modal_update_project_user_notes() )

		jQuery( '[data-action="toggle-approval"]' ).on( 'click',  ( e ) => project.toggle_approval( e ) )
		jQuery( '[data-action="remove-approval"]' ).on( 'click',  () => project.init_remove_approval() )


    }

	init_dropdowns() {
		jQuery( '#form-edit-project-estimates .dropdown' ).dropdown(
			{
				allowAdditions: true,
				hideAdditions: false,
				clearable: true,
			}
		)

		jQuery( '#form-edit-project-offer-estimates .dropdown' ).dropdown(
			{
				allowAdditions: true,
				hideAdditions: false,
				clearable: true,
			}
		)

		jQuery( '.meta-list-menu .dropdown' ).dropdown(
			{
				action: 'hide', // selection doesn't overwrite the dropdown label.
			}
		)

		jQuery( '#remaining-work-status' ).dropdown()

		jQuery( '#extra-work-type' ).dropdown(
			{
				onChange: ( value, text, $choice ) => {
					console.debug( 'extra-work-type onchange', value, text, $choice, gt_terms?.extra_work_type?.support )
					const title = jQuery( '#extra-work-title' )

					if ( value === gt_terms?.extra_work_type?.support?.term_id.toString() ) {
						project.show_extra_work_project_fields()
					} else {
						project.hide_extra_work_project_fields()
					}

					if ( value === gt_terms?.extra_work_type?.commuting_time?.term_id.toString() ) {
						title.val( 'Fahrzeit' )
					} else {
						title.val( '' )
					}
				}
			}
		)

		jQuery( '#extra-work-type-edit' ).dropdown(
			{
				onChange: ( value, text, $choice ) => {
					console.debug( 'extra-work-type-edit onchange', value, text, $choice, gt_terms?.extra_work_type?.support )

					if ( value === gt_terms?.extra_work_type?.support?.term_id.toString() ) {
						project.show_extra_work_project_fields_edit()
					} else {
						project.hide_extra_work_project_fields_edit()
					}
				}
			}
		)

		jQuery( '#extra-work-project-status' ).dropdown()
		jQuery( '#extra-work-project-status-edit' ).dropdown()

		jQuery( '#extra-work-project-id-dropdown' ).dropdown()
		jQuery( '#extra-work-project-id-edit-dropdown' ).dropdown()

		jQuery( '#extra-work-duration-dropdown' ).dropdown(
			{
				allowAdditions: true,
				hideAdditions: false,
				clearable: true,
			}
		)

		jQuery( '#extra-work-duration-edit-dropdown' ).dropdown(
			{
				allowAdditions: true,
				hideAdditions: false,
				clearable: true,
			}
		)

		jQuery( '#remaining-work-status-approve-dropdown' ).dropdown()

        jQuery( '#project-data-shape' ).dropdown()
        jQuery( '#project-data-step-shape' ).dropdown()
        jQuery( '#project-data-wood-surcharges' ).dropdown()


	}

	init_checkboxes() {
		jQuery( '#remaining-work-priority' ).closest( '.checkbox' ).checkbox()
		jQuery( '#remaining-work-update-assembly' ).closest( '.checkbox' ).checkbox()

        jQuery( '#form-update-project-data .invisible.checkbox' ).checkbox(
            {
                uncheckable: true,
            }
        )

        jQuery( '#project-return-parts-required-reset-checkbox' ).checkbox()
        jQuery( '#project-return-parts-required-update-checkbox' ).checkbox()
	}

	init_progress() {
		jQuery( '#planning-estimates' ).progress(
			{
				label: 'ratio',
				duration: 1000,
				text: {
					ratio: '{percent}%',
					active: '{value} / {total}',
					success: 'alle Schätzzeiten vollständig!'
				},
				onChange: ( percent, value, total ) => {
					console.debug( 'estimates onchange', percent, value, total )
				},
			}
		)

        jQuery( '#planning-offer-estimates' ).progress(
            {
                label: 'ratio',
                duration: 1000,
                text: {
                    ratio: '{percent}%',
                    active: '{value} / {total}',
                    success: 'alle Angebotszeiten vollständig!'
                },
                onChange: ( percent, value, total ) => {
                    console.debug( 'offer estimates onchange', percent, value, total )
                },
            }
        )

		jQuery( '#planning-tasks' ).progress(
			{
				label: 'ratio',
				duration: 1000,
				text: {
					ratio: '{percent}%',
					active: '{value} / {total}',
					success: 'alle Aufgaben erledigt!'
				},
				onChange: ( percent, value, total ) => {
					console.debug( 'tasks onchange', percent, value, total )
				},
			}
		)
	}

	init_forms() {
		jQuery( '#form-edit-project-estimates' ).form(
			{
				fields: {
					'project-estimate-cutting-edit': {
						optional: true,
						rules: [
							{
								type: 'regExp[/^([0-9])*:[0-5][0-9]$/]',
								prompt: 'Bitte das Format h:mm für die Dauer beachten.'
							}
						]
					},
					'project-estimate-cnc-edit': {
						optional: true,
						rules: [
							{
								type: 'regExp[/^([0-9])*:[0-5][0-9]$/]',
								prompt: 'Bitte das Format h:mm für die Dauer beachten.'
							}
						]
					},
					'project-estimate-fitting-edit': {
						optional: true,
						rules: [
							{
								type: 'regExp[/^([0-9])*:[0-5][0-9]$/]',
								prompt: 'Bitte das Format h:mm für die Dauer beachten.'
							}
						]
					},
					'project-estimate-finishing-edit': {
						optional: true,
						rules: [
							{
								type: 'regExp[/^([0-9])*:[0-5][0-9]$/]',
								prompt: 'Bitte das Format h:mm für die Dauer beachten.'
							}
						]
					},
				},
				errorFocus: false,
				revalidate: true,
				onSuccess: ( e ) => project.update_estimates( e ),
				onFailure: app.show_toast_invalid_form, // wichtig: return false nötig, sonst Submit Event.
			}
		)

		jQuery( '#form-edit-project-offer-estimates' ).form(
			{
				fields: {
					'project-offer-estimate-cutting-edit': {
						optional: true,
						rules: [
							{
								type: 'regExp[/^([0-9])*:[0-5][0-9]$/]',
								prompt: 'Bitte das Format h:mm für die Dauer beachten.'
							}
						]
					},
					'project-offer-estimate-cnc-edit': {
						optional: true,
						rules: [
							{
								type: 'regExp[/^([0-9])*:[0-5][0-9]$/]',
								prompt: 'Bitte das Format h:mm für die Dauer beachten.'
							}
						]
					},
					'project-offer-estimate-fitting-edit': {
						optional: true,
						rules: [
							{
								type: 'regExp[/^([0-9])*:[0-5][0-9]$/]',
								prompt: 'Bitte das Format h:mm für die Dauer beachten.'
							}
						]
					},
					'project-offer-estimate-finishing-edit': {
						optional: true,
						rules: [
							{
								type: 'regExp[/^([0-9])*:[0-5][0-9]$/]',
								prompt: 'Bitte das Format h:mm für die Dauer beachten.'
							}
						]
					},
				},
				errorFocus: false,
				revalidate: true,
				onSuccess: ( e ) => project.update_offer_estimates( e ),
				onFailure: app.show_toast_invalid_form, // wichtig: return false nötig, sonst Submit Event.
			}
		)

		jQuery( '#form-measurement-date-edit' ).form(
			{
				fields: {
					'project-measurement-date-edit': {
						rules: [
							{
								type: 'notEmpty',
								prompt: 'Bitte ein Datum angeben. Falls du das Datum löschen möchtest, wähle die Aktion "löschen".'
							}
						]
					}
				},
				errorFocus: false,
				revalidate: true,
				onSuccess: ( e ) => project.update_task_date( e ),
				onFailure: app.show_toast_invalid_form, // wichtig: return false nötig, sonst Submit Event.
			}
		)

		jQuery( 'button[data-action="reset-project-measurement-date"]' ).on( 'click', ( e ) => project.reset_task_date( e ) )
		jQuery( 'button[data-action="delete-project-measurement-date"]' ).on( 'click', ( e ) => project.delete_task_date_confirmation( e ) )

		jQuery( '#form-construction-plans-date-edit' ).form(
			{
				fields: {
					'project-construction-plans-date-edit': {
						rules: [
							{
								type: 'notEmpty',
								prompt: 'Bitte ein Datum angeben. Falls du das Datum löschen möchtest, wähle die Aktion "löschen".'
							}
						]
					}
				},
				errorFocus: false,
				revalidate: true,
				onSuccess: ( e ) => project.update_task_date( e ),
				onFailure: app.show_toast_invalid_form, // wichtig: return false nötig, sonst Submit Event.
			}
		)

		jQuery( 'button[data-action="reset-project-construction-plans-date"]' ).on( 'click', ( e ) => project.reset_task_date( e ) )
		jQuery( 'button[data-action="delete-project-construction-plans-date"]' ).on( 'click', ( e ) => project.delete_task_date_confirmation( e ) )

		jQuery( '#form-add-extra-work' ).form(
			{
				fields: {
					'extra-work-type': {
						rules: [
							{
								type: 'notEmpty',
								prompt: 'Bitte einen Grund angeben.'
							}
						]
					},
					'extra-work-title': {
						rules: [
							{
								type: 'notEmpty',
								prompt: 'Bitte einen Titel angeben.'
							}
						]
					},
					'extra-work-duration': {
						rules: [
                            {
                                type: 'regExp[/^([0-9])*:[0-5][0-9]$/]',
                                prompt: 'Bitte das Format h:mm für die Dauer beachten.'
                            },
                            {
								type: 'notEmpty',
								prompt: 'Bitte eine Dauer in h:mm angeben.'
							}
						]
					},
				},
				errorFocus: false,
				revalidate: true,
				onSuccess: ( e ) => project.add_extra_work( e ),
				onFailure: app.show_toast_invalid_form, // wichtig: return false nötig, sonst Submit Event.
			}
		)

		jQuery( '#form-edit-extra-work' ).form(
			{
				fields: {
					'extra-work-type-edit': {
						rules: [
							{
								type: 'notEmpty',
								prompt: 'Bitte einen Grund angeben.'
							}
						]
					},
					'extra-work-title-edit': {
						rules: [
							{
								type: 'notEmpty',
								prompt: 'Bitte einen Titel angeben.'
							}
						]
					},
					'extra-work-duration-edit': {
						rules: [
                            {
                                type: 'regExp[/^([0-9])*:[0-5][0-9]$/]',
                                prompt: 'Bitte das Format h:mm für die Dauer beachten.'
                            },
							{
								type: 'notEmpty',
								prompt: 'Bitte eine Dauer in h:mm angeben.'
							}
						]
					},
				},
				errorFocus: false,
				revalidate: true,
				onSuccess: ( e ) => project.update_extra_work( e ),
				onFailure: app.show_toast_invalid_form, // wichtig: return false nötig, sonst Submit Event.
			}
		)

		jQuery( '#form-edit-extra-work-thumbnail' ).form(
			{
				fields: {
					'extra-work-thumbnail-edit': {
						rules: [
							{
								type: 'notEmpty',
								prompt: 'Bitte ein Bild auswählen.'
							}
						]
					},
				},
				errorFocus: false,
				revalidate: true,
				onSuccess: ( e ) => project.update_extra_work_thumbnail( e ),
				onFailure: app.show_toast_invalid_form,
			}
		)

		jQuery( '#form-edit-extra-work-images' ).form(
			{
				fields: {
					'extra-work-images-edit': {
						rules: [
							{
								type: 'notEmpty',
								prompt: 'Bitte mindestens ein Bild auswählen.'
							}
						]
					},
				},
				errorFocus: false,
				revalidate: true,
				onSuccess: ( e ) => project.update_extra_work_images( e ),
				onFailure: app.show_toast_invalid_form,
			}
		)

		jQuery( '#form-add-remaining-work' ).form(
			{
				fields: {
					'remaining-work-status': {
						rules: [
							{
								type: 'notEmpty',
								prompt: 'Bitte einen Bereich angeben.'
							}
						]
					},
					'remaining-work-title': {
						rules: [
							{
								type: 'notEmpty',
								prompt: 'Bitte einen Titel angeben.'
							}
						]
					},
				},
				errorFocus: false,
				revalidate: true,
				onSuccess: ( e ) => project.add_remaining_work( e ),
				onFailure: app.show_toast_invalid_form, // wichtig: return false nötig, sonst Submit Event.
			}
		)

		jQuery( '#form-edit-remaining-work' ).form(
			{
				fields: {
					'remaining-work-title-edit': {
						rules: [
							{
								type: 'notEmpty',
								prompt: 'Bitte einen Titel angeben.'
							}
						]
					},
				},
				errorFocus: false,
				revalidate: true,
				onSuccess: ( e ) => project.update_remaining_work( e ),
				onFailure: app.show_toast_invalid_form, // wichtig: return false nötig, sonst Submit Event.
			}
		)

		jQuery( '#form-approve-remaining-work' ).form(
			{
				fields: {
					'remaining-work-status-approve': {
						rules: [
							{
								type: 'notEmpty',
								prompt: 'Bitte einen Bereich angeben.'
							}
						]
					}
				},
				errorFocus: false,
				revalidate: true,
				onSuccess: ( e ) => project.approve_remaining_work( e ),
				onFailure: app.show_toast_invalid_form, // wichtig: return false nötig, sonst Submit Event.
			}
		)

		jQuery( '#form-edit-remaining-work-thumbnail' ).form(
			{
				fields: {
					'remaining-work-thumbnail-edit': {
						rules: [
							{
								type: 'notEmpty',
								prompt: 'Bitte ein Bild auswählen.'
							}
						]
					},
				},
				errorFocus: false,
				revalidate: true,
				onSuccess: ( e ) => project.update_remaining_work_thumbnail( e ),
				onFailure: app.show_toast_invalid_form,
			}
		)

		jQuery( '#form-edit-remaining-work-images' ).form(
			{
				fields: {
					'remaining-work-images-edit': {
						rules: [
							{
								type: 'notEmpty',
								prompt: 'Bitte mindestens ein Bild auswählen.'
							}
						]
					},
				},
				errorFocus: false,
				revalidate: true,
				onSuccess: ( e ) => project.update_remaining_work_images( e ),
				onFailure: app.show_toast_invalid_form,
			}
		)

		jQuery( '#form-add-project-documents' ).form(
			{
				errorFocus: false,
				onSuccess: ( e ) => project.add_project_documents( e ),
				onFailure: app.show_toast_invalid_form, // wichtig: return false nötig, sonst Submit Event.
			}
		)

		jQuery( '#form-project-document-edit' ).form(
			{
				fields: {
					'project-document-title-edit': {
						rules: [
							{
								type: 'notEmpty',
								prompt: 'Bitte einen Titel angeben. Falls du den Titel löschen möchtest, wähle die Aktion "löschen".'
							}
						]
					}
				},
				errorFocus: false,
				revalidate: true,
				onSuccess: ( e ) => project.update_project_document( e ),
				onFailure: app.show_toast_invalid_form, // wichtig: return false nötig, sonst Submit Event.
			}
		)

		jQuery( '#form-add-transfer-documents' ).form(
			{
				errorFocus: false,
				onSuccess: ( e ) => project.add_transfer_documents( e ),
				onFailure: app.show_toast_invalid_form, // wichtig: return false nötig, sonst Submit Event.
			}
		)

		jQuery( '#form-transfer-document-edit' ).form(
			{
				fields: {
					'transfer-document-title-edit': {
						rules: [
							{
								type: 'notEmpty',
								prompt: 'Bitte einen Titel angeben. Falls du den Titel löschen möchtest, wähle die Aktion "löschen".'
							}
						]
					}
				},
				errorFocus: false,
				revalidate: true,
				onSuccess: ( e ) => project.update_transfer_document( e ),
				onFailure: app.show_toast_invalid_form, // wichtig: return false nötig, sonst Submit Event.
			}
		)

		jQuery( '#form-project-storage-yard-assembly' ).form(
			{
				fields: {
					'project-storage-yard-edit': {
						rules: [
							{
								type: 'notEmpty',
								prompt: 'Bitte einen Lagerplatz für die Montage angeben.'
							}
						]
					},
				},
				errorFocus: false,
				revalidate: true,
				onSuccess: ( e ) => project.update_storage_yard_assembly( e ),
				onFailure: app.show_toast_invalid_form, // wichtig: return false nötig, sonst Submit Event.
			}
		)

		jQuery( '#form-project-storage-yard-edit' ).form(
			{
				errorFocus: false,
				onSuccess: ( e ) => project.update_storage_yard( e ),
				onFailure: app.show_toast_invalid_form, // wichtig: return false nötig, sonst Submit Event.
			}
		)

		jQuery( '#form-project-deadline-edit' ).form(
			{
				fields: {
					'project-deadline-edit': {
						rules: [
							{
								type: 'notEmpty',
								prompt: 'Bitte ein Datum angeben.'
							}
						]
					}
				},
				errorFocus: false,
				revalidate: true,
				onSuccess: ( e ) => project.update_deadline( e ),
				onFailure: app.show_toast_invalid_form, // wichtig: return false nötig, sonst Submit Event.
			}
		)

		jQuery( '#form-project-user-notes-edit' ).form(
			{
				errorFocus: false,
				onSuccess: ( e ) => project.update_project_user_notes( e ),
				onFailure: app.show_toast_invalid_form, // wichtig: return false nötig, sonst Submit Event.
			}
		)

        jQuery( '#form-update-project-data' ).form(
            {
                errorFocus: false,
                revalidate: true,
                onSuccess: ( e: Event ) => project.update_project_data( e ),
                onFailure: app.show_toast_invalid_form, // wichtig: return false nötig, sonst Submit Event.
            }
        )

        jQuery( '#form-project-return-update' ).form(
            {
                fields: {
                    'project-return-reason-update': {
                        rules: [
                            {
                                type: 'notEmpty',
                                prompt: 'Bitte einen Grund für die Reklamation angeben.'
                            }
                        ]
                    }
                },
                errorFocus: false,
                revalidate: true,
                onSuccess: ( e: Event ) => {
                    e.preventDefault()

                    void project.status_controller.api_update_project_return()
                },
                onFailure: Toast.show_invalid_form,
            }
        )
	}

	init_popups() {
		jQuery( '.status-todo' )
			.popup()

		jQuery( '.approval-container .info' )
			.popup(
				{
					popup: jQuery( '.status-info-container' ),
				}
			)

		jQuery( '.temp-approvals .info' )
			.popup(
				{
					popup: jQuery( '.temp-approvals-info-container' ),
				}
			)

		jQuery( '.current-status .info' )
			.popup(
				{
					popup: jQuery( '.current-status-meta' ),
				}
			)

		jQuery( '.project-schedule-container .status' )
			.popup(
				{
					popup: jQuery( '.project-schedule-container .tooltip' ),
					position: 'bottom center',
				}
			)

		jQuery( '.project-schedule-container .status' )
			.popup(
				{
					popup: jQuery( '.project-schedule-container .tooltip' ),
					position: 'bottom center',
				}
			)
	}

	init_calendars() {
		const today = new Date();

		jQuery( '#date-measurement-date-edit' )
			.calendar(
				{
					type: 'date',
					selectAdjacentDays: true,
					firstDayOfWeek: 1,
					disabledDaysOfWeek: [ 0, 6 ],
					showWeekNumbers: true,
					monthFirst: false,
					minDate: today,
					formatter: {
						date: 'DD.MM.YYYY',
					},
					text: app.calendar_text,
					popupOptions: {
						position: 'top left',
					},
					onChange: ( date, text, mode ) => {
						console.debug( 'date-measurement-date-edit onchange', date, text, mode )
					}
				}
			)

		jQuery( '#date-construction-plans-date-edit' )
			.calendar(
				{
					type: 'date',
					selectAdjacentDays: true,
					firstDayOfWeek: 1,
					disabledDaysOfWeek: [ 0, 6 ],
					showWeekNumbers: true,
					monthFirst: false,
					minDate: today,
					formatter: {
						date: 'DD.MM.YYYY',
					},
					text: app.calendar_text,
					popupOptions: {
						position: 'top left',
					},
					onChange: ( date, text, mode ) => {
						console.debug( 'date-construction-plans-date-edit onchange', date, text, mode )
					}
				}
			)

		jQuery( '#date-project-deadline-edit' )
			.calendar(
				{
					type: 'date',
					selectAdjacentDays: true,
					firstDayOfWeek: 1,
					disabledDaysOfWeek: [ 0, 6 ],
					showWeekNumbers: true,
					monthFirst: false,
					minDate: today,
					formatter: {
						date: 'DD.MM.YYYY',
					},
					text: app.calendar_text,
					popupOptions: {
						position: 'top left',
					},
					onChange: ( date, text, mode ) => {
						console.debug( 'date-project-deadline-edit onchange', date, text, mode )
					}
				}
			)
	}

	init_tabs() {
		this.tabs = jQuery( '.estimates-wrapper .menu .item' )
        this.tabs.tab()
	}

    init_nags() {}

	get_meta( key ) {
		return this.meta.get( key )
	}

	get_meta_value( key ) {
		const meta = this.get_meta( key )

		if ( ! meta ) {
			return null
		}

		return meta.get_value() ?? null
	}

	has_meta( key ) {
		if ( ! this.meta.has( key ) ) {
			return false
		}

		return this.get_meta( key ).value != null
	}

	set_meta( key, value, required = false ) {
		if ( this.has_meta( key ) ) {
			return this.get_meta( key ).set_value( value )
		}

		return this.meta.set( key, value )
	}

	init_meta( key, value ) {
		switch ( key ) {
			case 'post-id':
				this.set_meta( key, new NumberMeta( key, value, true ) )
				break
			case 'project-title':
				this.set_meta( key, new TitleMeta( key, value, true ) )
				break
			case 'project-assembly':
				this.set_meta( key, new AssemblyMeta( key, value ) )
				break
			case 'project-price':
				this.set_meta( key, new PriceMeta( key, value, true ) )
				break
			case 'project-customer-street':
			case 'project-customer-zip-location':
			case 'project-customer-tel':
				this.set_meta( key, new ProjectMeta( key, value, true ) )
				break
			case 'project-customer-email':
				this.set_meta( key, new EmailMeta( key, value ) )
				break
			case 'project-global-notes':
				this.set_meta( key, new NotesMeta( key, value ) )
				break
			case 'project-planning-estimates':
				this.set_meta( key, new EstimatesMeta( key, value ) )
				break
			case 'project-planning-offer-estimates':
				this.set_meta( key, new OfferEstimatesMeta( key, value ) )
				break
            case 'project-times-net':
                this.set_meta( key, new TimesNetMeta( key, value ) )
                break
            case 'project-status-reasons':
                this.set_meta( key, new StatusReasonsMeta( key, value ) )
                break
			case 'project-properties':
				this.set_meta( key, new PropertyMeta( key, value ) )
				break
			case 'project-measurement':
			case 'project-construction-plans':
				this.set_meta( key,  new TaskDateMeta( key, value ) )
				break
			case 'project-status':
				this.set_meta( key, new StatusMeta( key, value, true ) )
				break
			case 'project-substatus':
				this.set_meta( key, new SubstatusMeta( key, value ) )
				break
			case 'project-status-workers':
				this.set_meta( key, new WorkersMeta( key, value ) )
				break
			case 'project-deadlines':
				this.set_meta( key, new DeadlineMeta( key, value ) )
				break;
			case 'project-storage-yards':
				this.set_meta( key, new StorageYardsMeta( key, value ) )
				break
			case 'project-approval-dates':
			case 'project-start-dates':
				this.set_meta( key, new Meta( key, value ) )
				break
			case 'project-surface-treatment':
				this.set_meta( key, new ProjectMeta( key, value ) )
				break
            case 'project-total-invoiced-amount':
                this.set_meta( key, new InvoiceMeta( key, value ) )
                break
            case 'project-measurement-id':
                this.set_meta( key, new NumberMeta( key, value ) )
                break
            case 'project-data':
                this.set_meta( key, new DataMeta( key, value ) )
                break
            case 'project-return-reason':
                this.set_meta( key, new NotesMeta( key, value ) )
                break
		}
	}

	render_meta( meta_item: string|null = null ) {
		console.log( 'render', meta_item )

		if ( ! meta_item ) {
			console.log( 'render failed' )
			return
		}

		this.meta.get( meta_item ).render()
	}

	render_metas( keys ) {
		for ( const key of keys ) {
			const meta = this.meta.get( key )
			meta.render()
		}
	}

	render_all_meta() {
		const metas = [
			'project-title',
			'project-assembly',
			'project-price',
			'project-customer-street',
			'project-customer-zip-location',
			'project-customer-tel',
			'project-customer-email',
			'project-global-notes',
			'project-planning-estimates',
			'project-planning-offer-estimates',
			'project-properties',
			'project-measurement',
			'project-construction-plans',
			'project-status',
			'project-substatus',
			'project-status-workers',
			'project-deadlines',
			'project-storage-yards',
			'project-surface-treatment',
			'project-total-invoiced-amount',
            'project-start-dates',
            'project-approval-dates',
            'project-data',
            'project-times-net',
		]

		this.render_metas( metas )
	}

	/**
	 * Aktualisiert Project Meta, welche nicht im Datenblatt sind.
	 * called after submitting forms
	 * => Goehler_Tool_Public_AJAX_Controller::update_project_meta()
	 *
	 * @param event
	 */
	update_form_meta( event ) {
		console.log( 'update project meta form', this, event.target )

		event.preventDefault()

		const form      = jQuery( this )
		const form_data = form.serialize()
		const action    = 'update_project_meta'

		if ( ! form.form( 'is valid' ) ) {
			app.show_toast_invalid_form()
			return
		}

		const { nonce, post_id, ajax_url } = gt_ajax_object

		const data = {
			action,
			form_data,
			nonce,
			post_id
		}

		console.log( 'AJAX data', data )

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				dataType: 'json',
				data,
				cache: false,
			}
		)
		.done(

			/**
			 * Erfolgreiche Antwort von Goehler_Tool_Public_AJAX_Controller::update_project_meta()
			 *
			 * @param response  AJAX response
			 *
			 * @var post_meta  Aktualisierte Metadaten (name, value)
			 */
			( response ) => {
				console.log( 'AJAX response', response )

				const { success, toast, post_meta } = response

				if ( ! success ) {
					Toast.show_ajax_error( response )
					return
				}

				app.show_toast_ajax_response( toast )

				if ( app.is_debug_enabled() ) {
					app.show_toast_ajax_response(
						`meta name: ${post_meta?.name}<br />meta value: ${post_meta.value}`,
						''
					)
				}

				update_meta_list_item( post_meta?.name, post_meta?.value )
			}
		)
	}

	/**
	 * Löscht ein Projekt (AJAX)
	 * => Goehler_Tool_Public_AJAX_Controller::ajax_delete_project()
	 *
	 * @var data.action	  delete_project
	 * @var data.post_id  Post ID des Projekts
	 */
	delete() {
		console.log( 'delete project' )

		const { ajax_url, nonce } = gt_ajax_object

		const post_id = this.post_id

		const data = {
			action: 'delete_project',
			post_id,
			nonce,
		}

		const dimmer = jQuery( '.full-screen-loading-dimmer .dimmer' )
		dimmer.dimmer( 'show' )

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				dataType: 'json',
				data,
				cache: false
			}
		)
		.done(

		/**
		 * Erfolgreiche Antwort von Goehler_Tool_Public_AJAX_Controller::ajax_delete_project()
		 *
		 * @param response  AJAX response
		 *
		 * @var redirect  URL der Weiterleitung nach erfolgreichem Löschen des Projekts
		 */
			( response ) => {
				console.log( 'AJAX response', response )

				const { redirect } = response

				if ( redirect ) {
					window.location.href = redirect
					return
				}

				dimmer.dimmer( 'hide' )

				app.show_toast_ajax_response(
					'Das Projekt konnte leider nicht gelöscht werden.',
					'error'
				)
			}
		);
	}

	/**
	 * Zeigt eine Abfrage, bevor das Projekt gelöscht wird.
	 */
	delete_confirmation() {
		console.log( 'delete project?', this.post_id )

		const confirmation = this.get_modal( 'delete-project-confirm' )

		confirmation.modal(
			{
				onApprove: () => {
					console.log( 'approved' )
					this.delete()
				}
			}
		)
		confirmation.modal( 'show' )
	}

	/**
	 * Hilfsfunktion um ein Modal-Element als jQuery-Objekt zu finden.
	 *
	 * @param selector	Selektor für das [data-modal] Attribut.
	 * @returns JQuery<HTMLElement>
	 */
	get_modal( selector: string ): JQuery<HTMLElement> {
		return jQuery( `.ui.modal[data-modal="${selector}"]` )
	}

	/**
	 * Hilfsfunktion um ein Meta-Modal-Element als jQuery-Objekt zu finden.
	 *
	 * @param selector   Selektor für das [data-project-meta] Attribut.
	 * @returns {*|null}
	 */
	get_meta_modal( selector = null ) {
		if ( ! selector ) {
			return null
		}

		return jQuery( `.ui.modal[data-project-meta="${selector}"]` )
	}

	/**
	 * PLANUNGSAUFGABEN (TASKS)
	 * - aktivieren und deaktivieren (bzw. erledigen)
	 */

	/**
	 * Wechselt bei einer Planungsaufgabe den Status zwischen erledigt und zu erledigen.
	 *
	 * @param item	Planungsaufgabe.
	 * @param e		Klick-Event.
	 */
	toggle_task( item, e ) {
		if ( item.classList.contains( 'loading' ) ) {
			return
		}

		if ( e.target.nodeName === 'BUTTON' ) {
			return
		}

		if ( e.target.className === 'date' ) {
			return
		}

		console.log( 'toggle project task', item )

		if ( item.classList.contains( 'completed' ) ) {
			this.remove_task( item )
		} else {
			this.add_task( item )
		}
	}

	/**
	 * Zeigt eine Abfrage, bevor das Projekt pausiert wird.
	 */
	pause_confirmation() {
		console.log( 'pause project?', this.post_id )

		const confirmation = this.get_modal( 'pause-project-confirm' )

		confirmation.modal(
			{
				onApprove: () => {
					console.log( 'approved' )
					this.pause()
				}
			}
		)
		confirmation.modal( 'show' )
	}

	/**
	 * Zeigt eine Abfrage, bevor das Projekt wieder fortgesetzt wird.
	 */
	unpause_confirmation() {
		console.log( 'unpause project?', this.post_id )

		const confirmation = this.get_modal( 'unpause-project-confirm' )

		confirmation.modal(
			{
				onApprove: () => {
					console.log( 'approved' )
					this.unpause()
				}
			}
		)
		confirmation.modal( 'show' )
	}

	/**
	 * Pausiert ein Projekt (AJAX)
	 * => Goehler_Tool_Public_AJAX_Controller::ajax_pause_project()
	 *
	 * @var data.action	  pause_project
	 * @var data.post_id  Post ID des Projekts
	 */
	pause() {
		console.debug( 'pause project' )

		const { ajax_url, nonce } = gt_ajax_object

		const post_id = this.post_id

		const data = {
			action: 'pause_project',
			post_id,
			nonce,
		}

		console.log( 'AJAX data', data )

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				dataType: 'json',
				data,
				cache: false
			}
		)
		.done(

			/**
			 * Erfolgreiche Antwort von Goehler_Tool_Public_AJAX_Controller::ajax_pause_project()
			 *
			 * @param response  AJAX response
			 *
			 * @var properties  Alle aktuellen Projektdaten
			 * @var toast       Text für die Benachrichtigung
			 */
			( response ) => {
				console.log( 'AJAX response', response )

				const { success, toast } = response

				if ( ! success ) {
					app.show_toast_ajax_error( response )
					return
				}

				app.show_toast_ajax_response( toast )

				project.update_properties_meta( response.project.properties )
				update_meta_list_item( 'project-assembly', response.project.assembly )
				update_meta_list_item( 'project-deadlines', response.project.deadlines )
				update_meta_list_item( 'project-construction-plans', response.project.construction_plans )
				update_meta_list_item( 'project-measurement', response.project.measurement )
				update_meta_list_item( 'project-storage-yards', response.project.storage_yards )
				project.render_pause()
				project.render_approvals()
			}
		)
	}

	/**
	 * Setzt ein Projekt fort (AJAX)
	 * => Goehler_Tool_Public_AJAX_Controller::ajax_unpause_project()
	 *
	 * @var data.action	  unpause_project
	 * @var data.post_id  Post ID des Projekts
	 */
	unpause() {
		console.debug( 'unpause project' )

		const { ajax_url, nonce } = gt_ajax_object

		const post_id = this.post_id

		const data = {
			action: 'unpause_project',
			post_id,
			nonce,
		}

		console.log( 'AJAX data', data )

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				dataType: 'json',
				data,
				cache: false
			}
		)
		.done(

			/**
			 * Erfolgreiche Antwort von Goehler_Tool_Public_AJAX_Controller::ajax_pause_project()
			 *
			 * @param response  AJAX response
			 *
			 * @var properties  Alle aktuellen Projektdaten
			 * @var toast	    Text für die Benachrichtigung
			 */
			( response ) => {
				console.log( 'AJAX response', response )

				const { properties, toast, success } = response

				if ( ! success ) {
					app.show_toast_ajax_error( response )
					return
				}

				app.show_toast_ajax_response( toast )

				project.update_properties_meta( properties )
				project.render_pause()
				project.render_approvals()
			}
		);
	}

	add_priority() {
		console.log( 'prioritize project' )

		const { ajax_url, nonce } = gt_ajax_object

		const post_id = this.post_id

		const data = {
			action: 'add_project_priority',
			post_id,
			nonce,
		}

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				dataType: 'json',
				data,
				cache: false
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				if ( ! response.success ) {
					app.show_toast_ajax_error( response )
					return
				}

				app.show_toast_ajax_response( response.toast )

				project.update_properties_meta( response.properties )
				project.render_priority()

				// TODO: was passiert noch nach priorisieren?
			}
		);
	}

	remove_priority() {
		console.log( 'remove project priority' )

		const { ajax_url, nonce } = gt_ajax_object

		const post_id = this.post_id

		const data = {
			action: 'remove_project_priority',
			post_id,
			nonce,
		}

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				dataType: 'json',
				data,
				cache: false
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				if ( ! response.success ) {
					app.show_toast_ajax_error( response )
					return
				}

				app.show_toast_ajax_response( response.toast )

				project.update_properties_meta( response.properties )
				project.render_priority()

				// TODO: was passiert noch nach entpriorisieren?
			}
		);
	}

	has_property( property ) {
		const has_prop = property in this.get_meta_value( 'project-properties' )
		console.log( 'has property', property, has_prop )

		return has_prop
	}

    /**
     * Rendert die Priorisierung des Projekts.
     */
    render_priority()  {
        const meta      = this.get_meta_value( 'project-properties' )
        const container = document.querySelector( '.meta-list-container' )

        container.dataset.prioritized = ( 'priority' in meta ? 'true' : 'false' )
    }

	render_pause()  {
		console.log( 'render pause' )

		const meta = this.get_meta_value( 'project-properties' )

		const message = document.querySelector( '[data-message="paused-project"]' )
		const title   = document.querySelector( '.meta-list-container' )

		const content  = document.querySelector( '.content.start-in-progress' )
		const status   = this.get_meta_value( 'project-status' )

		if ( 'paused' in meta ) {
			title.dataset.paused   = 'true'
			message.dataset.hidden = 'false'

			if ( content ) {
				content.dataset.dimmerStatus = 'blocked'
			}
		} else {
			title.dataset.paused   = 'false'
			message.dataset.hidden = 'true'

			if ( content && status !== action_tab ) {
				if ( status.slug !== action_tab ) {
					content.dataset.dimmerStatus = 'blocked'
				} else {
					content.dataset.dimmerStatus = 'allowed'
				}
			}
		}
	}

	update_properties_meta( properties ) {
		console.log( 'update project properties', properties )
		const meta = this.get_meta( 'project-properties' )
		this.set_meta( 'project-properties', properties )
		meta.render()
	}

	init_modal_update_measurement_date() {
		const selector = 'project-measurement-date'
		console.log( 'init meta modal:', selector )

		const modal      = this.get_meta_modal( selector )
		const meta       = this.get_meta_value( 'project-measurement' )
		const reset_btn  = jQuery( 'button[data-action="reset-project-measurement-date"]' )
		const delete_btn = jQuery( 'button[data-action="delete-project-measurement-date"]' )
		const messages   = modal.find( '.ui.message:not(.error)' )
		const assembly   = this.get_meta_value( 'project-assembly' )

		// resets.
		reset_btn.attr( 'data-hidden', 'true' )
		delete_btn.attr( 'data-hidden', 'true' )
		messages.addClass( 'hidden' )

		// sets.
		jQuery( '#date-measurement-date-edit' ).calendar( 'set date', meta.date ?? '' )

		if ( meta.locked ) {
			modal.find( '.ui.message[data-date-meta="locked"]' ).removeClass( 'hidden' )

			if ( assembly.type.length ) {
				reset_btn.removeAttr( 'data-hidden' )
			} else {
				delete_btn.removeAttr( 'data-hidden' )
			}
		}

		if ( meta.synced ) {
			modal.find( '.ui.message[data-date-meta="synced"]' ).removeClass( 'hidden' )
		}

		modal.modal( { autofocus: false } ).modal( 'show' )
	}

	init_modal_update_construction_plans_date() {
		const selector = 'project-construction-plans-date'
		console.log( 'init meta modal:', selector )

		const modal      = this.get_meta_modal( selector )
		const meta       = this.get_meta( 'project-construction-plans' ).get_value()
		const reset_btn  = jQuery( 'button[data-action="reset-project-construction-plans-date"]' )
		const delete_btn = jQuery( 'button[data-action="delete-project-construction-plans-date"]' )
		const messages   = modal.find( '.ui.message:not(.error)' )
		const assembly   = this.get_meta_value( 'project-assembly' )

		// resets.
		reset_btn.attr( 'data-hidden', 'true' )
		delete_btn.attr( 'data-hidden', 'true' )
		messages.addClass( 'hidden' )

		// sets.
		jQuery( '#date-construction-plans-date-edit' ).calendar( 'set date', meta.date ?? '' )

		if ( meta.locked ) {
			modal.find( '.ui.message[data-date-meta="locked"]' ).removeClass( 'hidden' )

			if ( assembly.type.length ) {
				reset_btn.removeAttr( 'data-hidden' )
			} else {
				delete_btn.removeAttr( 'data-hidden' )
			}
		}

		if ( meta.synced ) {
			modal.find( '.ui.message[data-date-meta="synced"]' ).removeClass( 'hidden' )
		}

		modal.modal( { autofocus: false } ).modal( 'show' )
	}

	set_dates( data ) {
		console.debug( 'set dates', data )

		this.dates = data
	}

	update_estimates( e ) {
		console.log( 'update project estimates', e.currentTarget )

		// enable AJAX.
		e.preventDefault()

		const form   = jQuery( e.currentTarget )
		const submit = jQuery( '.submit', form )
		const inputs = jQuery( '.ui.input', form )

		// disable form submit button.
		submit.attr( 'disabled', 'disabled' );
		inputs.addClass( 'disabled' )

		const { nonce, ajax_url } = gt_ajax_object

		const form_data = form.serialize()

		const data = {
			action: 'update_project_estimates',
			form_data,
			nonce
		}

		console.log( data )

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				dataType: 'json',
				data,
				cache: false
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				if ( response.error ) {
					app.show_toast_ajax_response( response.toast, 'error' )
					return
				}

				app.show_toast_ajax_response( response.toast )

				const meta = project.get_meta( 'project-planning-estimates' )
				meta.set_value( response.project?.planning_estimates )
				meta.update()

				update_meta_list_item( 'project-properties', response.project?.properties )
				update_meta_list_item( 'project-deadlines', response.project?.deadlines )
				project.render_project_schedule()
                project.render_approvals()

				submit.removeAttr( 'disabled' );
				inputs.removeClass( 'disabled' )

                if ( 'estimates-complete' in response.project.properties ) {
                    app.play_confetti( 'stars' )
                }
			}
		)
	}

	update_offer_estimates( e ) {
		console.log( 'update project offer estimates', e.currentTarget )

		// enable AJAX.
		e.preventDefault()

		const form   = jQuery( e.currentTarget )
		const submit = jQuery( '.submit', form )
		const inputs = jQuery( '.ui.input', form )

		// disable form submit button.
		submit.attr( 'disabled', 'disabled' );
		inputs.addClass( 'disabled' )

		const { nonce, ajax_url } = gt_ajax_object

		const form_data = form.serialize()

		const data = {
			action: 'update_project_offer_estimates',
			form_data,
			nonce
		}

		console.log( data )

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				dataType: 'json',
				data,
				cache: false
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				if ( response.error ) {
					app.show_toast_ajax_response( response.toast, 'error' )
					return
				}

				app.show_toast_ajax_response( response.toast )

				const meta = project.get_meta( 'project-planning-offer-estimates' )
				meta.set_value( response.project?.planning_offer_estimates )
				meta.update()

				update_meta_list_item( 'project-properties', response.project?.properties )

                project.render_approvals()

				submit.removeAttr( 'disabled' );
				inputs.removeClass( 'disabled' )

                if ( 'offer-estimates-complete' in response.project.properties ) {
                    app.play_confetti( 'money' )
                }
			}
		)
	}

	add_task( target ) {
		console.log( 'add planning task', target )

		const action    = 'add_project_task'
		const form_data = {
			term_id: target.dataset.termId
		}

		const tasks = document.querySelectorAll( '.tasks-container .task' )

		const { nonce, post_id, ajax_url } = gt_ajax_object

		for ( const task of tasks ) {
			task.classList.add( 'loading' )
		}

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				dataType: 'json',
				data: { action, form_data, nonce, post_id },
				cache: false,
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				for ( const task of tasks ) {
					task.classList.remove( 'loading' )
				}

				if ( ! response.success ) {
					app.show_toast_ajax_error( response )
					return
				}

				app.show_toast_ajax_response( response.toast )

				// update term data.
				target.classList.add( 'completed' )

				const properties = project.get_meta( 'project-properties' )
				properties.set_value( response.properties )
				properties.update_tasks_progress()

                console.log( 'confetti?', response.properties, response.properties.length )

                const required_tasks = [
                    'measurement-complete',
                    'customer-approval-complete',
                    'construction-plans-complete',
                    'production-approval-complete',
                    'staff-meeting-complete',
                ]

                if ( required_tasks.every( key => key in response.properties ) ) {
                    app.play_confetti( 'stars' )
                }

				project.render_task_buttons()
			}
		)
	}

	remove_task( target ) {
		console.log( 'remove planning task', target )

		const action    = 'remove_project_task'
		const form_data = {
			term_id: target.dataset.termId
		}

		const tasks = document.querySelectorAll( '.tasks-container .task' )

		const { nonce, post_id, ajax_url } = gt_ajax_object

		for ( const task of tasks ) {
			task.classList.add( 'loading' )
		}

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				dataType: 'json',
				data: { action, form_data, nonce, post_id },
				cache: false,
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )
				const delay = 0

				setTimeout(
					() => {
						for ( const task of tasks ) {
							task.classList.remove( 'loading' )
						}
					},
					delay
				)

				if ( ! response.success ) {
					app.show_toast_ajax_error( response )
					return
				}

				app.show_toast_ajax_response( response.toast )

				setTimeout(
					() => {
						target.classList.remove( 'completed' )
					},
					delay
				)

				const properties = project.get_meta( 'project-properties' )
				properties.set_value( response.properties )
				properties.update_tasks_progress()

				project.render_task_buttons()
			}
		)
	}

	update_task_date( e ) {
		console.log( 'update task date', e )

		e.preventDefault()

		const form = jQuery( e.currentTarget )

		const action    = 'update_project_task_date'
		const form_data = form.serialize()

		const tasks = document.querySelectorAll( '.tasks-container .task' )

		const { nonce, post_id, ajax_url } = gt_ajax_object

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				dataType: 'json',
				data: { action, form_data, nonce, post_id },
				cache: false,
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				for ( const task of tasks ) {
					task.classList.remove( 'loading' )
				}

				if ( ! response.success ) {
					app.show_toast_ajax_error( response )
					return
				}

				if ( ! response.meta?.key ) {
					app.show_toast_ajax_error( 'missing meta key' )
					return
				}

				app.show_toast_ajax_response( response.toast )

				const meta = project.get_meta( response.meta.key )

				console.log( meta )

				if ( meta ) {
					meta.set_value( response.meta.value )
					meta.render()
				}

				const modal = form.closest( '.ui.modal' )
				modal.modal( 'hide' )
			}
		)
	}

	delete_task_date_confirmation( e ) {
		console.log( 'delete task date?', e.currentTarget )

		const confirmation = project.get_modal( 'delete-project-task-date-confirm' )

		confirmation.modal(
			{
				onApprove: () => {
					console.log( 'approved' )
					this.delete_task_date( e )
				}
			}
		)
		confirmation.modal( 'show' )
	}

	delete_task_date( e ) {
		console.log( 'delete task date', e.currentTarget )

		const button      = e.currentTarget
		const task_action = button.dataset.action

		if ( ! task_action ) {
			return
		}

		const tasks = document.querySelectorAll( '.tasks-container .task' )

		const action = 'delete_project_task_date'

		const { nonce, post_id, ajax_url } = gt_ajax_object

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				dataType: 'json',
				data: { action, nonce, post_id, task_action },
				cache: false,
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				for ( const task of tasks ) {
					task.classList.remove( 'loading' )
				}

				if ( ! response.success ) {
					app.show_toast_ajax_error( response )
					return
				}

				if ( ! response.meta?.key ) {
					app.show_toast_ajax_error( 'missing meta key' )
					return
				}

				app.show_toast_ajax_response( response.toast )

				const meta = project.get_meta( response.meta.key )

				console.log( meta )

				if ( meta ) {
					meta.set_value( response.meta.value )
					meta.render()
				}

				const modal = jQuery( button.closest( '.ui.modal' ) )
				modal.modal( 'hide' )
			}
		)
	}

	reset_task_date( e ) {
		console.log( 'reset task date', e.currentTarget )

		const button      = e.currentTarget
		const task_action = button.dataset.action

		if ( ! task_action ) {
			return
		}

		const tasks = document.querySelectorAll( '.tasks-container .task' )

		const action = 'reset_project_task_date'

		const { nonce, post_id, ajax_url } = gt_ajax_object

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				dataType: 'json',
				data: { action, nonce, post_id, task_action },
				cache: false,
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				for ( const task of tasks ) {
					task.classList.remove( 'loading' )
				}

				if ( ! response.success ) {
					app.show_toast_ajax_error( response )
					return
				}

				if ( ! response.meta?.key ) {
					app.show_toast_ajax_error( 'missing meta key' )
					return
				}

				app.show_toast_ajax_response( response.toast )

				const meta = project.get_meta( response.meta.key )

				console.log( meta )

				if ( meta ) {
					meta.set_value( response.meta.value )
					meta.render()
				}

				const modal = jQuery( button.closest( '.ui.modal' ) )
				modal.modal( 'hide' )
			}
		)
	}

	render_task_buttons() {
		const measurement = this.get_meta( 'project-measurement' )
		measurement.render()

		const construction_plans = this.get_meta( 'project-construction-plans' )
		construction_plans.render()
	}

	calculate_estimate_progress( estimates ) {
		const values   = Object.values( estimates )
		const filtered = values.filter( el => el !== '' )

		return filtered.length
	}

    calculate_offer_estimate_progress( estimates ) {
        const values   = Object.values( estimates )
        const filtered = values.filter( el => el !== '' )

        return filtered.length
    }

	calculate_tasks_progress() {
		const meta     = this.get_meta( 'project-properties' )
		const filtered = Object.keys( meta.get_value() ).filter( property => [ 'measurement-complete', 'customer-approval-complete', 'construction-plans-complete', 'production-approval-complete', 'staff-meeting-complete' ].includes( property ) )

		return filtered.length
	}

	show_extra_work_project_fields() {
		console.log( 'show extra work project fields' )
		const container = document.querySelector( '#extra-work-project-fields' )
		if ( container ) {
			container.dataset.hidden = ''

			jQuery( '#form-add-extra-work' )
				.form(
					'add rule',
					'extra-work-project-id',
					{
						rules: [
							{
								type: 'notEmpty',
								prompt: 'Bitte ein Projekt angeben.'
							}
						]
					}
				)
				.form(
					'add rule',
					'extra-work-project-status',
					{
						rules: [
							{
								type: 'notEmpty',
								prompt: 'Bitte einen Bereich angeben.'
							}
						]
					}
				)
		}
	}

	hide_extra_work_project_fields() {
		console.log( 'hide extra work project fields' )
		const container = document.querySelector( '#extra-work-project-fields' )
		if ( container ) {
			container.dataset.hidden = 'true'

			jQuery( '#form-add-extra-work' )
				.form( 'remove rule', 'extra-work-project-status-id' )
				.form( 'remove rule', 'extra-work-project-status-status' )
		}

		jQuery( '#extra-work-project-id-dropdown' ).dropdown( 'clear' )
		jQuery( '#extra-work-project-status' ).dropdown( 'clear' )
	}

	show_extra_work_project_fields_edit() {
		console.log( 'show extra work project fields edit' )
		const container = document.querySelector( '#extra-work-project-fields-edit' )
		if ( container ) {
			container.dataset.hidden = ''

			jQuery( '#form-edit-extra-work' )
				.form(
					'add rule',
					'extra-work-project-id-edit',
					{
						rules: [
							{
								type: 'notEmpty',
								prompt: 'Bitte ein Projekt angeben.'
							}
						]
					}
				)
				.form(
					'add rule',
					'extra-work-project-status-edit',
					{
						rules: [
							{
								type: 'notEmpty',
								prompt: 'Bitte einen Bereich angeben.'
							}
						]
					}
				)
		}
	}

	hide_extra_work_project_fields_edit() {
		console.log( 'hide extra work project fields edit' )
		const container = document.querySelector( '#extra-work-project-fields-edit' )
		if ( container ) {
			container.dataset.hidden = 'true'

			jQuery( '#form-edit-extra-work' )
				.form( 'remove rule', 'extra-work-project-id-edit' )
				.form( 'remove rule', 'extra-work-project-status-edit' )
		}

		jQuery( '#extra-work-project-id-edit-dropdown' ).dropdown( 'clear' )
		jQuery( '#extra-work-project-status-edit' ).dropdown( 'clear' )
	}

	init_modal_add_extra_work() {
		console.log( 'init modal: add extra work', this.post_id )

		const modal  = jQuery( '[data-modal="add-extra-work"]' )
		const form   = jQuery( 'form', modal )
		const fields = jQuery( '#extra-work-project-fields' )

		// resets.
		form.form( 'reset' )
		fields.attr( 'data-hidden', 'true' )

		modal.modal( { autofocus: false } ).modal( 'show' )
	}

	add_extra_work( e ) {
		console.log( 'add new extra work', e )

		// enable AJAX.
		e.preventDefault()

		const form = jQuery( e.currentTarget )

		const submit = jQuery( '.submit', form )
		submit.attr( 'disabled', 'disabled' )
		submit.addClass( 'loading' )

		const { ajax_url, nonce, post_id } = gt_ajax_object

		const data = new FormData( form[0] )
		data.append( 'action', 'add_extra_work' )
		data.append( 'nonce', nonce )
		data.append( 'post-id', post_id )
		data.append( 'status', action_tab )

		console.debug( 'AJAX data', form.serialize() )

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				data,
				cache: false,
				contentType: false,
				processData: false,
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				if ( response.error ) {
					app.show_toast_ajax_response( response.toast, 'error' )
					return
				}

				app.show_toast_ajax_response( response.toast )

				submit.removeAttr( 'disabled' )
				submit.removeClass( 'loading' )

				project.set_extra_work( response.project_extra_work ?? [] )
                app.close_modals()
				project.render_extra_work()
                project.times_controller.render_status_timetable()
                project.times_controller.render_status_times()
			}
		);
	}

	set_extra_work( data ) {
		console.debug( 'set extra work', data )

		if ( action_tab === 'data' ) {
			this.extra_work = data
			return
		}

		this.extra_work = data.filter( el => el.status.slug === action_tab )
	}

	set_support( data ) {
		console.debug( 'set support', data )

		if ( action_tab === 'data' ) {
			this.support = data
			return
		}

		this.support = data.filter( el => el.support.project_status.slug === action_tab )
	}

	render_extra_work() {
		console.debug( 'render extra work', this.extra_work.length )

		const container = document.querySelector( '.action-container[data-post-type="extra-work"]' )

		if ( ! container ) {
			return
		}

		const items    = this.extra_work
		const count_el = container.querySelector( '.count' )
		const list_el  = container.querySelector( '.item-list' )
		const actions  = container.querySelector( '.view-actions' )
		const count    = items.length
		let minutes    = 0

		console.debug( 'all extra work', this.extra_work )

		list_el.innerHTML = ''

		actions.dataset.hidden = 'true'

		if ( count > 0 ) {
			delete actions.dataset.hidden

			for ( const item of items.toReversed() ) {
				const li = document.createElement( 'li' )

				li.classList.add( 'list-item', 'extra-work-item', 'collapsed' )
				li.dataset.postId = item.post_id

				li.innerHTML = `
				<div class="item-image">
					<i class="image icon" data-action="update-extra-work-thumbnail"></i>
				</div>
				
				<div class="item-title">
					<div class="title">${item.title}</div>
					
					<div class="content"></div>
					
					<div class="details">
						<span>
							<i class="user icon"></i> ${item.author.name}
						</span>
						
						<span>
							<i class="tag icon"></i> ${item.type.name}
						</span>
					</div>
					
				</div>
				
				<div class="item-info" data-project-status="${item.status.slug}">
					<span class="time">
						<i class="stopwatch icon"></i> ${item.duration} h
					</span>
				</div>
				
				<div class="item-icon angle">
					<i class="angle left icon"></i>
				</div>
				
				<div class="item-images"></div>

				<div class="item-actions">
					<a class="ui tiny primary button debug" href="/wp-admin/post.php?post=${item.post_id}&action=edit" 
					target="_blank" data-debug data-hidden="${( gt_user_settings.enable_debug === '1' ? 'false' : 'true' )}">Post bearbeiten</a>
					
					<button class="ui tiny button" data-action="update-extra-work-item">
						bearbeiten
					</button>
					
					<button class="ui tiny button" data-action="update-extra-work-thumbnail">
						Vorschaubild bearbeiten
					</button>
					
					<button class="ui tiny button" data-action="update-extra-work-images">
						weitere Bilder bearbeiten
					</button>
				</div>
				`

				if ( item.thumbnail != null ) {
					const image = li.querySelector( '.item-image' )
					image.innerHTML = `
						<a href="${item.thumbnail.url.full}" class="extra-work-thumbnail">
							<img src="${item.thumbnail.url.thumbnail}" loading="lazy" alt="">
						</a>`
				}

				if ( item.type.slug === 'support' ) {
					const details = li.querySelector( '.item-title .details' )
					const title = item.support.project.title

					details.innerHTML += `
						<span>
							<i class="project diagram icon"></i> 
							${title} 
							<i class="long arrow alternate right icon"></i>
							${item.support.project_status.name} 
						</span>
					`
				}

				if ( action_tab === 'data' ) {
					const content = li.querySelector( '.item-title .content' )

					content.innerHTML += `
						<div class="status">
							<i class="folder icon"></i> ${item.status.name}
						</div>
					`
				}

                if ( 'return' in item.properties ) {
                    const content = li.querySelector( '.item-title .content' )

                    if ( content ) {
                        content.innerHTML += '<div class="returned-property"><i class="undo icon"></i> Reklamation</div>'
                    }
                }

				if ( item.images.length ) {
					const container = li.querySelector( '.item-images' )

					for ( const image of item.images ) {
						container.innerHTML += `<a href="${image.url.full}" class="extra-work-thumbnail">
							<img src="${image.url.thumbnail}" loading="lazy" alt="">
						</a>`
					}
				}

				list_el.append( li )

				minutes += duration_in_minutes( item.duration )
			}
		}

		count_el.innerHTML = `${minutes_to_duration( minutes )} Stunden`

		console.debug( 'extra work total minutes', minutes )
	}

	init_modal_update_extra_work( item ) {
		const post_id = Number( item.dataset.postId )
		const post    = this.extra_work.find( el => el.post_id === post_id )
		console.log( 'update extra work', post_id, post )

		const modal = jQuery( '[data-modal="edit-extra-work"]' )

		const title    = jQuery( '#extra-work-title-edit' )
		const duration       = jQuery( '#extra-work-duration-edit-dropdown' )
		const type           = jQuery( '#extra-work-type-edit' ).closest( '.dropdown' )
		const project_status = jQuery( '#extra-work-project-status-edit' ).closest( '.dropdown' )
		const project_id     = jQuery( '#extra-work-project-id-edit-dropdown' )

		const form = jQuery( '#form-edit-extra-work' )

		// resets.
		form.form( 'reset' )

		// sets.
		jQuery( '.extra-work-post-id' ).val( post.post_id )
		title.val( post.title )
		type.dropdown( 'set selected', post.type.term_id )

		project_id.dropdown( 'set selected', post.support?.project?.id ?? '' )
		project_status.dropdown( 'set selected', post.support?.project_status.slug ?? '' )

		if ( post.duration === '0:00' ) {
			duration.dropdown( 'clear' )
		} else {
			duration.dropdown( 'set selected', post.duration )
		}

		modal.modal( { autofocus: false } ).modal( 'show' )
	}

	update_extra_work( e ) {
		console.log( 'update extra work', e.currentTarget )

		// enable AJAX.
		e.preventDefault()

		const form      = jQuery( e.currentTarget )
		const form_data = form.serialize()

		const form_buttons  = jQuery( '.form-actions button', form )
		const submit_button = jQuery( '.submit', form )
		form_buttons.attr( 'disabled', 'disabled' )
		submit_button.addClass( 'loading elastic' )

		const { nonce, ajax_url } = gt_ajax_object

		const data = {
			action: 'update_extra_work',
			form_data,
			nonce
		}

		console.log( 'AJAX data', data )

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				dataType: 'json',
				data,
				cache: false
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				if ( response.error ) {
					app.show_toast_ajax_response( response.toast, 'error' )
					return
				}

				app.show_toast_ajax_response( response.toast )

				form_buttons.removeAttr( 'disabled' )
				submit_button.removeClass( 'loading elastic' )

				project.update_extra_work_data( response.post_id, response.post_data ?? [] )
                app.close_modals()
				project.render_extra_work()
                project.times_controller.render_status_timetable()
                project.times_controller.render_status_times()
			}
		)
	}

	delete_extra_work_confirmation( e ) {
		console.log( 'delete extra work?', e.currentTarget )

		const confirmation = project.get_modal( 'delete-extra-work-confirm' )

		confirmation.modal(
			{
				onApprove: () => {
					console.log( 'approved' )
					this.delete_extra_work( e )
				}
			}
		)
		confirmation.modal( 'show' )
	}

	delete_extra_work( e ) {
		console.log( 'delete extra work', e.currentTarget )

		const form    = jQuery( e.currentTarget.closest( 'form' ) )
		const post_id = form.find( '[name="post-id"]' ).val()

		const { nonce, ajax_url } = gt_ajax_object

		const data = {
			action: 'delete_extra_work',
			post_id,
			nonce
		}

		console.log( 'AJAX data', data )

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				dataType: 'json',
				data,
				cache: false
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				if ( response.error ) {
					app.show_toast_ajax_response( response.toast, 'error' )
					return
				}

				app.show_toast_ajax_response( response.toast )

				project.set_extra_work( response.project_extra_work ?? [] )
                app.close_modals()
				project.render_extra_work()
                project.times_controller.render_status_timetable()
                project.times_controller.render_status_times()
			}
		)
	}

	add_highlight_extra_work( post_id ) {
		console.debug( 'highlight extra work', post_id )

		const container = document.querySelector( '.action-container[data-post-type="extra-work"]' )

		if ( ! container ) {
			return
		}

		const item = container.querySelector( `.list-item[data-post-id="${post_id}"]` )
		item.classList.add( 'highlighted' )
	}

	remove_highlight_extra_work( post_id ) {
		console.debug( 'unhighlight extra work', post_id )

		const container = document.querySelector( '.action-container[data-post-type="extra-work"]' )

		if ( ! container ) {
			return
		}

		const item = container.querySelector( `.list-item[data-post-id="${post_id}"]` )
		item.classList.remove( 'highlighted' )
	}

	init_modal_update_extra_work_thumbnail( item ) {
		const post_id = Number( item.dataset.postId )
		const post    = this.extra_work.find( el => el.post_id === post_id )
		console.log( 'update extra work image', post_id, post )

		const modal = this.get_modal( 'edit-extra-work-thumbnail' )

		const current_image = jQuery( '#extra-work-thumbnail-preview' )
		const filename      = jQuery( '#extra-work-thumbnail-filename' )
		const current_info  = modal.find( '.current-image-info' )
		const no_image      = modal.find( '.no-image' )

		// resets.
		modal.form( 'reset' )
		current_image.prop( 'src', '' ).hide()

		// sets.
		jQuery( '.extra-work-post-id' ).val( post.post_id )

		if ( post.thumbnail ) {
			current_info.show()
			current_image.prop( 'src', post.thumbnail.url.medium ).show()
			filename.val( ( post.thumbnail ? post.thumbnail.filename : '' ) ).show()
			modal.find( '.delete' ).show()
			no_image.hide()
		} else {
			current_info.hide()
			modal.find( '.delete ' ).hide()
			no_image.show()
		}

		modal.modal( { autofocus: false } ).modal( 'show' )
	}

	init_modal_update_extra_work_images( item ) {
		const post_id = Number( item.dataset.postId )
		const post    = this.extra_work.find( el => el.post_id === post_id )
		console.log( 'update extra work images', post_id, post )

		const modal = this.get_modal( 'edit-extra-work-images' )

		const current_images = document.querySelector( '#form-edit-extra-work-images .current-images' )
		const no_image       = modal.find( '.no-image' )

		// resets.
		modal.form( 'reset' )
		current_images.innerHTML = ''

		// sets.
		jQuery( '.extra-work-post-id' ).val( post.post_id )

		if ( post.images.length ) {
			for ( const image of post.images ) {
				current_images.innerHTML += `
				<div class="item-image" data-post-id="${image.post_id}">
					<div class"image_inner">
						<img src="${image.url.medium}" />
						<div class="image-meta">
							<div class="ui disabled input">
								<input type="text" disabled class="form-control" value="${image.filename}">
							</div>
							<button class="ui icon red button" type="button" data-action="delete-current-image">
								<i class="trash alternate icon"></i>
							</button>
						</div>
					</div>
				</div>`
			}

			modal.find( '.delete' ).show()
			no_image.hide()
		} else {
			modal.find( '.delete ' ).hide()
			no_image.show()
		}

		modal.modal( { autofocus: false } ).modal( 'show' )
	}

	update_extra_work_images( e ) {
		console.log( 'update extra work images', e.currentTarget )

		// enable AJAX.
		e.preventDefault()

		const form = jQuery( e.currentTarget )

		const form_buttons  = jQuery( '.form-actions button', form )
		const submit_button = jQuery( '.submit', form )
		form_buttons.attr( 'disabled', 'disabled' )
		submit_button.addClass( 'loading elastic' )

		const { nonce, ajax_url } = gt_ajax_object

		const data = new FormData( form[0] )
		data.append( 'action', 'update_extra_work_images' )
		data.append( 'nonce', nonce )

		console.log( 'AJAX data', data )

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				data,
				cache: false,
				contentType: false,
				processData: false,
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				if ( response.error ) {
					app.show_toast_ajax_response( response.toast, 'error' )
					return
				}

				app.show_toast_ajax_response( response.toast )

				form_buttons.removeAttr( 'disabled' )
				submit_button.removeClass( 'loading elastic' )

				project.update_extra_work_data( response.post_id, response.post_data )
                app.close_modals()
				project.render_extra_work()
			}
		)
	}

	update_extra_work_thumbnail( e ) {
		console.log( 'update extra work image', e.currentTarget )

		// enable AJAX.
		e.preventDefault()

		const form = jQuery( e.currentTarget )

		const form_buttons  = jQuery( '.form-actions button', form )
		const submit_button = jQuery( '.submit', form )
		form_buttons.attr( 'disabled', 'disabled' )
		submit_button.addClass( 'loading elastic' )

		const { nonce, ajax_url } = gt_ajax_object

		const data = new FormData( form[0] )
		data.append( 'action', 'update_extra_work_thumbnail' )
		data.append( 'nonce', nonce )

		console.log( 'update extra work image', data )

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				data,
				cache: false,
				contentType: false,
				processData: false,
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				if ( response.error ) {
					app.show_toast_ajax_response( response.toast, 'error' )
					return
				}

				app.show_toast_ajax_response( response.toast )

				form_buttons.removeAttr( 'disabled' )
				submit_button.removeClass( 'loading elastic' )

				project.update_extra_work_data( response.post_id, response.post_data )
                app.close_modals()
				project.render_extra_work()
                project.times_controller.render_status_timetable()
			}
		)
	}

	delete_extra_work_image( target ) {
		console.log( 'delete extra work image', target )

		const form    = jQuery( target.closest( 'form' ) )
		const post_id = form.find( '[name="post-id"]' ).val()
		const image_id = target.dataset.postId

		const { nonce, ajax_url } = gt_ajax_object

		const data = {
			action: 'delete_extra_work_image',
			post_id,
			image_id,
			nonce
		}

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				dataType: 'json',
				data,
				cache: false
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				if ( response.error ) {
					app.show_toast_ajax_response( response.toast, 'error' )
					return
				}

				app.show_toast_ajax_response( response.toast )

				project.update_extra_work_data( response.post_id, response.post_data )
                app.close_modals()
				project.render_extra_work()
			}
		)
	}

	delete_extra_work_images( e ) {
		console.log( 'delete extra work images', e.currentTarget )

		const form    = jQuery( e.currentTarget.closest( 'form' ) )
		const post_id = form.find( '[name="post-id"]' ).val()

		const { nonce, ajax_url } = gt_ajax_object

		const data = {
			action: 'delete_extra_work_images',
			post_id,
			nonce
		}

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				dataType: 'json',
				data,
				cache: false
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				if ( response.error ) {
					app.show_toast_ajax_response( response.toast, 'error' )
					return
				}

				app.show_toast_ajax_response( response.toast )

				project.update_extra_work_data( response.post_id, response.post_data )
                app.close_modals()
				project.render_extra_work()
			}
		)
	}

	delete_extra_work_thumbnail_confirmation( e ) {
		console.log( 'delete extra work image?', e.currentTarget )

		const confirmation = project.get_modal( 'delete-extra-work-thumbnail-confirm' )

		confirmation.modal(
			{
				onApprove: () => {
					console.log( 'approved' )
					this.delete_extra_work_thumbnail( e )
				}
			}
		)
		confirmation.modal( 'show' )
	}

	delete_extra_work_thumbnail( e ) {
		console.log( 'delete extra work image', e.currentTarget )

		const form    = jQuery( e.currentTarget.closest( 'form' ) )
		const post_id = form.find( '[name="post-id"]' ).val()

		const { nonce, ajax_url } = gt_ajax_object

		const data = {
			action: 'delete_extra_work_thumbnail',
			post_id,
			nonce
		}

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				dataType: 'json',
				data,
				cache: false
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				if ( response.error ) {
					app.show_toast_ajax_response( response.toast, 'error' )
					return
				}

				app.show_toast_ajax_response( response.toast )

				project.update_extra_work_data( response.post_id, response.post_data )
                app.close_modals()
				project.render_extra_work()
                project.times_controller.render_status_timetable()
			}
		)
	}

	/**
	 * Aktualisiert die Daten einer Mehrarbeit mit neuen Daten.
	 *
	 * @param id 	Post ID der Mehrarbeit
	 * @param data	Array mit Daten der aktualisierten Mehrarbeit.
	 * @returns {boolean}
	 */
	update_extra_work_data( id, data ) {
		console.log( 'update extra work data', id, data )

		if ( ! id ) {
			console.log( 'keine ID' )
			return false
		}

		const index = this.extra_work.findIndex( el => el.post_id === Number( id ) )

		if ( index < 0 ) {
			console.log( `keine Mehrarbeit mit der ID ${id} gefunden` )
			return false
		}

		this.extra_work[index] = data
		return true
	}

	/**
	 * Öffnet und initialisiert das Modal zum Anschauen eines Bildes einer Mehrarbeit.
	 *
	 * @param target	Auslöser für das Click-Event. In der Regel das verlinkte Bild aus der Liste der Mehrarbeiten.
	 */
	init_modal_view_extra_work_image( target ) {
		console.log( 'view image', target )
		const modal = jQuery( '[data-modal="view-extra-work-image"]' )

		const link    = target.closest( 'a' )
		const img_url = link.href
		const image   = jQuery( '#view-extra-work-image' )

		image.prop( 'src', '' )
		image.prop( 'src', img_url )
		modal.modal( { autofocus: false } ).modal( 'show' )
	}

	expand_extra_work() {
		jQuery( '.extra-work-item' ).removeClass( 'collapsed' )
	}

	collapse_extra_work() {
		jQuery( '.extra-work-item' ).addClass( 'collapsed' )
	}

	/**
	 * RESTARBEITEN
	 * - Datensatz ersetzen
	 * - rendern
	 */

	sort_remaining_work() {

		// falsch sortieren, da noch .toReversed folgt.
		this.remaining_work?.sort(
			( a, b ) => {
				if ( 'priority' in a.properties && 'priority' in b.properties ) {
					return 0;
				}

				if ( 'priority' in a.properties ) {
					return 1;
				}

				if ( 'priority' in b.properties ) {
					return -1;
				}
			}
		)
	}

	/**
	 * Ersetzt alle mit dem Projekt verknüpften Restarbeiten mit einem neuen Datensatz von Mehrarbeiten.
	 * In der Regel geschieht das, nachdem eine Restarbeit erstellt oder gelöscht wurde.
	 *
	 * @param data    Array mit neuem Datensatz von Restarbeiten des Projekts.
	 */
	set_remaining_work( data ) {
		console.debug( 'set remaining work', data )

		if ( action_tab === 'data' ) {
			this.remaining_work = data
			return
		}

		this.remaining_work = data.filter(
			el => {
				if ( el.status.slug === 'external' ) {
					return action_tab === 'assembly'
				}

				return el.status.slug === action_tab
			}
		)
	}

	/**
	 * Rendert alle Restarbeiten eines Projekts in einer Liste. Restarbeiten können dabei erstellt, bearbeitet oder gelöscht werden.
	 */
	render_remaining_work() {
		console.debug( 'render remaining work', this.remaining_work.length )

		const container = document.querySelector( '.action-container[data-post-type="remaining-work"]' )

		if ( ! container ) {
			return
		}

		const items    = this.remaining_work
		const count_el = container.querySelector( '.count' )
		const list_el  = container.querySelector( '.item-list' )
		const actions  = container.querySelector( '.view-actions' )
		const count    = items.length

		console.debug( 'all remaining work', this.remaining_work )

		count_el.innerHTML = count.toString()
		list_el.innerHTML  = ''

		actions.dataset.hidden = 'true'

		if ( count > 0 ) {
			delete actions.dataset.hidden

			this.sort_remaining_work()

			for ( const item of items.toReversed() ) {
				const li = document.createElement( 'li' )

				li.classList.add( 'list-item', 'remaining-work-item', 'collapsed' )
				li.dataset.postId = item.post_id

				li.innerHTML = `
				<div class="item-image">
					<i class="image icon" data-action="update-remaining-work-thumbnail"></i>
				</div>
				<div class="item-title">
					<div class="title">${item.title}</div>
					
					<div class="content">
						<div class="description">${item.description}</div>
					</div>
					
					<div class="details">
						<span>
							<i class="user icon"></i> ${item.author.name}
						</span>
					</div>
				</div>
				
				<div class="item-info">
					<button class="ui tiny button" data-action="approve-remaining-work-item">
						freigeben
					</button>
					<span class="status badge success">abgeschlossen</span>
				</div>
				
				<div class="item-icon angle">
					<i class="angle left icon"></i>
				</div>
				
				<div class="item-images"></div>
				
				<div class="item-actions">
						<a class="ui tiny primary button debug" href="/wp-admin/post.php?post=${item.post_id}&action=edit" 
						target="_blank" data-debug data-hidden="${( gt_user_settings.enable_debug === '1' ? 'false' : 'true' )}">Post bearbeiten</a>
	
						<button class="ui tiny button" data-action="update-remaining-work-item">
							bearbeiten
						</button>
						
						<button class="ui tiny button" data-action="update-remaining-work-thumbnail">
							Vorschaubild bearbeiten
						</button>
						
						<button class="ui tiny button" data-action="update-remaining-work-images">
							weitere Bilder bearbeiten
						</button>
						
						<button class="ui tiny button" data-action="show-remaining-work-status-log">
							Freigabeverlauf
						</button>
					</div>	
				`

				if ( item.thumbnail != null ) {
					const image = li.querySelector( '.item-image' )
					image.innerHTML = `
						<a href="${item.thumbnail.url.full}" class="remaining-work-thumbnail">
							<img src="${item.thumbnail.url.thumbnail}" loading="lazy" alt="">
						</a>`
				}

				if ( action_tab === 'data' ) {
					const content = li.querySelector( '.item-title .content' )

					content.innerHTML += `
						<div class="status">
							<i class="folder icon"></i> ${ item.status.name }
						</div>
					`
				}

				if ( action_tab === 'assembly' && item.status.slug === 'external' ) {
					const content = li.querySelector( '.item-title .content' )

					content.innerHTML += `
						<span>
							<i class="folder icon"></i> ${ item.status.name }
						</span>
					`
				}

				if ( item.status.slug === 'completed' ) {
					li.classList.add( 'completed' )
				}

                if ( 'return' in item.properties ) {
                    const content = li.querySelector( '.item-title .content' )

                    if ( content ) {
                        content.innerHTML += '<div class="returned-property"><i class="undo icon"></i> Reklamation</div>'
                    }

                }

				if ( 'priority' in item.properties ) {
					li.classList.add( 'prioritized' )
					const title = li.querySelector( '.item-title .title' )

					title.innerHTML = `<i class="blue star icon"></i> ${title.innerHTML}`
				}


				if ( item.images.length ) {
					const container = li.querySelector( '.item-images' )

					for ( const image of item.images ) {
						container.innerHTML += `<a href="${image.url.full}" class="remaining-work-thumbnail">
							<img src="${image.url.thumbnail}" loading="lazy" alt="">
						</a>`
					}
				}

				list_el.append( li )
			}
		}
	}

	init_modal_add_remaining_work() {
		console.log( 'init modal: add remaining work', this.post_id )

		const modal = jQuery( '[data-modal="add-remaining-work"]' )
		const form  = jQuery( 'form', modal )

		// resets.
		form.form( 'reset' )

		modal.modal( { autofocus: false } ).modal( 'show' )
	}

	/**
	 * Erstellt eine neue Restarbeit.
	 *
	 * @param e
	 */
	add_remaining_work( e ) {
		console.log( 'add new remaining work', e )

		// enable AJAX.
		e.preventDefault()

		const form = jQuery( e.currentTarget )

		const submit = jQuery( '.submit', form )
		submit.attr( 'disabled', 'disabled' )
		submit.addClass( 'loading' )

		const { ajax_url, nonce, post_id } = gt_ajax_object

		const data = new FormData( form[0] )
		data.append( 'action', 'add_remaining_work' )
		data.append( 'nonce', nonce )
		data.append( 'post-id', post_id )

		console.log( 'AJAX data', data )

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				data,
				cache: false,
				contentType: false,
				processData: false,
			}
		)
		.done(
			( response ) => {
				console.log( 'AJAX response', response )

				submit.removeAttr( 'disabled' )
				submit.removeClass( 'loading' )

				const { error, toast, project_remaining_work, project_properties, has_remaining_work } = response

				if ( error ) {
					app.show_toast_ajax_response( toast, 'error' )
					return
				}

				app.show_toast_ajax_response( response.toast )

				project.show_remaining_work_property_toast( has_remaining_work )

				project.set_remaining_work( project_remaining_work ?? [] )
				project.update_properties_meta( project_properties )
                app.close_modals()
				project.render_remaining_work()

				if ( response.update_assembly ) {
					const modal = this.get_modal( 'update-project-assembly' )
					modal?.modal( { autofocus: false } ).modal( 'show' )
				}
			}
		)
	}

	/**
	 * Öffnet und initialisiert das Modal zum Anschauen eines Bildes einer Restarbeit.
	 *
	 * @param target	Auslöser für das Click-Event. In der Regel das verlinkte Bild aus der Liste der Restarbeiten.
	 */
	init_modal_view_remaining_work_image( target ) {
		console.log( 'view image', target )
		const modal = jQuery( '[data-modal="view-remaining-work-image"]' )

		const link    = target.closest( 'a' )
		const img_url = link.href
		const image   = jQuery( '#view-remaining-work-image' )

		image.prop( 'src', '' )
		image.prop( 'src', img_url )
		modal.modal( { autofocus: false } ).modal( 'show' )
	}

	init_modal_update_remaining_work_thumbnail( item ) {
		const post_id = Number( item.dataset.postId )
		const post    = this.remaining_work.find( el => el.post_id === post_id )
		console.log( 'update remaining work thumbnail', post_id, post )

		const modal = this.get_modal( 'edit-remaining-work-thumbnail' )

		const current_image = jQuery( '#remaining-work-thumbnail-preview' )
		const filename      = jQuery( '#remaining-work-thumbnail-filename' )
		const current_info  = modal.find( '.current-image-info' )
		const no_image      = modal.find( '.no-image' )

		// resets.
		modal.form( 'reset' )
		current_image.prop( 'src', '' ).hide()

		// sets.
		jQuery( '.remaining-work-post-id' ).val( post.post_id )

		if ( post.thumbnail ) {
			current_info.show()
			current_image.prop( 'src', post.thumbnail.url.medium ).show()
			filename.val( ( post.thumbnail ? post.thumbnail.filename : '' ) ).show()
			modal.find( '.delete' ).show()
			no_image.hide()
		} else {
			current_info.hide()
			modal.find( '.delete ' ).hide()
			no_image.show()
		}

		modal.modal( { autofocus: false } ).modal( 'show' )
	}

	init_modal_update_remaining_work_images( item ) {
		const post_id = Number( item.dataset.postId )
		const post    = this.remaining_work.find( el => el.post_id === post_id )
		console.log( 'update remaining work images', post_id, post )

		const modal = this.get_modal( 'edit-remaining-work-images' )

		const current_images = document.querySelector( '#form-edit-remaining-work-images .current-images' )
		const no_image       = modal.find( '.no-image' )

		// resets.
		modal.form( 'reset' )
		current_images.innerHTML = ''

		// sets.
		jQuery( '.remaining-work-post-id' ).val( post.post_id )

		if ( post.images.length ) {
			for ( const image of post.images ) {
				current_images.innerHTML += `
				<div class="item-image" data-post-id="${image.post_id}">
					<div class"image_inner">
						<img src="${image.url.medium}" />
						<div class="image-meta">
							<div class="ui disabled input">
								<input type="text" disabled class="form-control" value="${image.filename}">
							</div>
							<button class="ui icon red button" type="button" data-action="delete-current-image">
								<i class="trash alternate icon"></i>
							</button>
						</div>
					</div>
				</div>`
			}

			modal.find( '.delete' ).show()
			no_image.hide()
		} else {
			modal.find( '.delete ' ).hide()
			no_image.show()
		}

		modal.modal( { autofocus: false } ).modal( 'show' )
	}

	update_remaining_work_images( e ) {
		console.log( 'update remaining work images', e.currentTarget )

		// enable AJAX.
		e.preventDefault()

		const form = jQuery( e.currentTarget )

		const form_buttons  = jQuery( '.form-actions button', form )
		const submit_button = jQuery( '.submit', form )
		form_buttons.attr( 'disabled', 'disabled' )
		submit_button.addClass( 'loading elastic' )

		const { nonce, ajax_url } = gt_ajax_object

		const data = new FormData( form[0] )
		data.append( 'action', 'update_remaining_work_images' )
		data.append( 'nonce', nonce )

		console.log( 'AJAX data', data )

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				data,
				cache: false,
				contentType: false,
				processData: false,
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				if ( response.error ) {
					app.show_toast_ajax_response( response.toast, 'error' )
					return
				}

				app.show_toast_ajax_response( response.toast )

				form_buttons.removeAttr( 'disabled' )
				submit_button.removeClass( 'loading elastic' )

				project.update_remaining_work_data( response.post_id, response.post_data )
                app.close_modals()
				project.render_remaining_work()
			}
		)
	}

	update_remaining_work_thumbnail( e ) {
		console.log( 'update remaining work thumbnail', e.currentTarget )

		// enable AJAX.
		e.preventDefault()

		const form = jQuery( e.currentTarget )

		const form_buttons  = jQuery( '.form-actions button', form )
		const submit_button = jQuery( '.submit', form )
		form_buttons.attr( 'disabled', 'disabled' )
		submit_button.addClass( 'loading elastic' )

		const { nonce, ajax_url } = gt_ajax_object

		const data = new FormData( form[0] )
		data.append( 'action', 'update_remaining_work_thumbnail' )
		data.append( 'nonce', nonce )

		console.log( 'AJAX data', data )

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				data,
				cache: false,
				contentType: false,
				processData: false,
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				if ( response.error ) {
					app.show_toast_ajax_response( response.toast, 'error' )
					return
				}

				app.show_toast_ajax_response( response.toast )

				form_buttons.removeAttr( 'disabled' )
				submit_button.removeClass( 'loading elastic' )

				project.update_remaining_work_data( response.post_id, response.post_data )
                app.close_modals()
				project.render_remaining_work()
			}
		)
	}

	delete_remaining_work_image( target ) {
		console.log( 'delete remaining work image', target )

		const form    = jQuery( target.closest( 'form' ) )
		const post_id = form.find( '[name="post-id"]' ).val()
		const image_id = target.dataset.postId

		const { nonce, ajax_url } = gt_ajax_object

		const data = {
			action: 'delete_remaining_work_image',
			post_id,
			image_id,
			nonce
		}

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				dataType: 'json',
				data,
				cache: false
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				if ( response.error ) {
					app.show_toast_ajax_response( response.toast, 'error' )
					return
				}

				app.show_toast_ajax_response( response.toast )

				project.update_remaining_work_data( response.post_id, response.post_data )
                app.close_modals()
				project.render_remaining_work()
			}
		)
	}

	delete_remaining_work_images( e ) {
		console.log( 'delete remaining work images', e.currentTarget )

		const form    = jQuery( e.currentTarget.closest( 'form' ) )
		const post_id = form.find( '[name="post-id"]' ).val()

		const { nonce, ajax_url } = gt_ajax_object

		const data = {
			action: 'delete_remaining_work_images',
			post_id,
			nonce
		}

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				dataType: 'json',
				data,
				cache: false
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				if ( response.error ) {
					app.show_toast_ajax_response( response.toast, 'error' )
					return
				}

				app.show_toast_ajax_response( response.toast )

				project.update_remaining_work_data( response.post_id, response.post_data )
                app.close_modals()
				project.render_remaining_work()
			}
		)
	}

	delete_remaining_work_thumbnail_confirmation( e ) {
		console.log( 'delete remaining work image?', e.currentTarget )

		const confirmation = project.get_modal( 'delete-remaining-work-thumbnail-confirm' )

		confirmation.modal(
			{
				onApprove: () => {
					console.log( 'approved' )
					this.delete_remaining_work_thumbnail( e )
				}
			}
		)
		confirmation.modal( 'show' )
	}

	delete_remaining_work_thumbnail( e ) {
		console.log( 'delete remaining work thumbnail', e.currentTarget )

		const form    = jQuery( e.currentTarget.closest( 'form' ) )
		const post_id = form.find( '[name="post-id"]' ).val()

		const { nonce, ajax_url } = gt_ajax_object

		const data = {
			action: 'delete_remaining_work_thumbnail',
			post_id,
			nonce
		}

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				dataType: 'json',
				data,
				cache: false
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				if ( response.error ) {
					app.show_toast_ajax_response( response.toast, 'error' )
					return
				}

				app.show_toast_ajax_response( response.toast )

				project.update_remaining_work_data( response.post_id, response.post_data )
                app.close_modals()
				project.render_remaining_work()
			}
		)
	}

	init_modal_update_remaining_work( item ) {
		const post_id = Number( item.dataset.postId )
		const post    = this.remaining_work.find( el => el.post_id === post_id )
		console.log( 'update remaining work', post_id, post )

		const modal = this.get_modal( 'edit-remaining-work' )

		const title = jQuery( '#remaining-work-title-edit' )
		const priority    = jQuery( '#remaining-work-priority-edit-checkbox' )

		const form = jQuery( '#form-edit-remaining-work' )

		// resets.
		form.form( 'reset' )

		// sets.
		jQuery( '.remaining-work-post-id' ).val( post.post_id )
		title.val( post.title )
		jQuery( '#remaining-work-description-edit' ).val( post.description ?? '' )

		console.log( post.properties, 'priority' in post.properties )

		if ( 'priority' in post.properties ) {
			priority.checkbox( 'check' )
		}

		modal.modal( { autofocus: false } ).modal( 'show' )
	}

	update_remaining_work( e ) {
		console.log( 'update remaining work', e.currentTarget )

		// enable AJAX.
		e.preventDefault()

		const form      = jQuery( e.currentTarget )
		const form_data = form.serialize()

		const form_buttons  = jQuery( '.form-actions button', form )
		const submit_button = jQuery( '.submit', form )
		form_buttons.attr( 'disabled', 'disabled' )
		submit_button.addClass( 'loading elastic' )

		const { nonce, ajax_url } = gt_ajax_object

		const data = {
			action: 'update_remaining_work',
			form_data,
			nonce
		}

		console.log( 'AJAX data', data )

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				dataType: 'json',
				data,
				cache: false
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				form_buttons.removeAttr( 'disabled' )
				submit_button.removeClass( 'loading elastic' )

				if ( response.success === false ) {
					app.show_toast_ajax_response( response.data.toast, 'error' )

					if ( app.is_debug_enabled() ) {
						app.show_toast_ajax_response( response.data.error, 'error' )
					}
					return
				}

				if ( response.error ) {
					app.show_toast_ajax_response( response.toast, 'error' )
					return
				}

				app.show_toast_ajax_response( response.toast )

				project.update_remaining_work_data( response.post_id, response.post_data ?? [] )
                app.close_modals()
				project.render_remaining_work()
			}
		)
	}

	/**
	 * Aktualisiert die Daten einer Restarbeit mit neuen Daten.
	 *
	 * @param id 	Post ID der Restarbeit
	 * @param data	Array mit Daten der aktualisierten Restarbeit.
	 * @returns {boolean}
	 */
	update_remaining_work_data( id, data ) {
		console.log( 'update remaining work data', id, data )

		if ( ! id ) {
			console.log( 'keine ID' )
			return false
		}

		const index = this.remaining_work.findIndex( el => el.post_id === Number( id ) )

		if ( index < 0 ) {
			console.log( `keine Restarbeit mit der ID ${id} gefunden` )
			return false
		}

		this.remaining_work[index] = data
		return true
	}

	init_modal_approve_remaining_work( item ) {
		const post_id = Number( item.dataset.postId )
		const post    = this.remaining_work.find( el => el.post_id === post_id )
		console.log( 'approve remaining work', post_id, post )

		const modal    = this.get_modal( 'approve-remaining-work' )
		const dropdown = jQuery( '#remaining-work-status-approve-dropdown' )

		console.log( dropdown )

		const form = jQuery( '#form-approve-remaining-work' )

		// resets.
		form.form( 'reset' )
		dropdown.find( '.item.disabled' ).removeClass( 'disabled' )

		// sets.
		jQuery( '.remaining-work-post-id' ).val( post.post_id )
		dropdown.find( `.item[data-value="${post.status.term_id}"]` ).addClass( 'disabled' )

		modal.modal( { autofocus: false } ).modal( 'show' )
	}

	expand_remaining_work() {
		jQuery( '.remaining-work-item' ).removeClass( 'collapsed' )
	}

	collapse_remaining_work() {
		jQuery( '.remaining-work-item' ).addClass( 'collapsed' )
	}

	approve_remaining_work( e ) {
		console.log( 'approve remaining work', e.currentTarget )

		// enable AJAX.
		e.preventDefault()

		const form      = jQuery( e.currentTarget )
		const form_data = form.serialize()

		const form_buttons  = jQuery( '.form-actions button', form )
		const submit_button = jQuery( '.submit', form )
		form_buttons.attr( 'disabled', 'disabled' )
		submit_button.addClass( 'loading elastic' )

		const { nonce, ajax_url } = gt_ajax_object

		const data = {
			action: 'approve_remaining_work',
			form_data,
			nonce
		}

		console.log( 'AJAX data', data )

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				dataType: 'json',
				data,
				cache: false
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				form_buttons.removeAttr( 'disabled' )
				submit_button.removeClass( 'loading elastic' )

				const { success, data, error, toast, project_remaining_work } = response

				if ( success === false ) {
					app.show_toast_ajax_response( data.toast, 'error' )

					if ( app.is_debug_enabled() ) {
						app.show_toast_ajax_response( data.error, 'error' )
					}
					return
				}

				if ( error ) {
					app.show_toast_ajax_response( toast, 'error' )
					return
				}

				app.show_toast_ajax_response( toast )

				project.set_remaining_work( project_remaining_work ?? [] )
                app.close_modals()
				project.render_remaining_work()
			}
		)
	}

	complete_remaining_work_confirmation( e ) {
		console.log( 'complete remaining work?', e.currentTarget )

		this.get_modal( 'approve-remaining-work' ).modal( 'hide' )

		const confirmation = this.get_modal( 'complete-remaining-work-confirm' )

		confirmation.modal(
			{
				onApprove: () => {
					console.log( 'approved' )
					this.complete_remaining_work()
				}
			}
		)
		confirmation.modal( 'show' )
	}

	complete_remaining_work() {
		const modal   = this.get_modal( 'approve-remaining-work' )
		const post_id = Number( modal.find( '.remaining-work-post-id' ).val() )

		console.log( 'complete remaining work', post_id )

		const { nonce, ajax_url } = gt_ajax_object

		const data = {
			action: 'complete_remaining_work',
			post_id,
			nonce
		}

		console.log( 'AJAX data', data )

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				dataType: 'json',
				data,
				cache: false
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				const { success, data, error, toast, project_remaining_work, project_properties, has_remaining_work } = response

				if ( success === false ) {
					app.show_toast_ajax_response( data.toast, 'error' )

					if ( app.is_debug_enabled() ) {
						app.show_toast_ajax_response( data.error, 'error' )
					}
					return
				}

				if ( error ) {
					app.show_toast_ajax_response( toast, 'error' )
					return
				}

				app.show_toast_ajax_response( toast )

				project.show_remaining_work_property_toast( has_remaining_work )

				project.set_remaining_work( project_remaining_work ?? [] )
				project.update_properties_meta( project_properties )
				project.render_remaining_work()
			}
		)
	}

	show_remaining_work_property_toast( has_remaining_work ) {
		if ( has_remaining_work ) {
			app.show_toast_ajax_response( 'Es sind aktuell offene Restarbeiten vorhanden.', '', 'info' )
		} else {
			app.show_toast_ajax_response( 'Keine offenen Restarbeiten mehr vorhanden.', '', 'info' )
		}
	}

	init_modal_remaining_work_status_log( item ) {
		console.log( 'init modal: remaining work status log', item )

		const post_id = Number( item.dataset.postId )
		const post    = this.remaining_work.find( el => el.post_id === post_id )

		console.log( post_id, post )

		const modal = this.get_modal( 'remaining-work-status-log' )

		const container = document.querySelector( '[data-modal="remaining-work-status-log"] .status-logs' )

		container.innerHTML = ''

		for ( const log of post.log ) {
			const log_item = this.render_remaining_work_status_log_item( log )
			container.append( log_item )
		}

		modal.modal( 'show' )
	}

	render_remaining_work_status_log_item( item ) {
		const div = document.createElement( 'div' )
		div.classList.add( 'log-item' )


		div.innerHTML = `
			<div class="status">
				<span class="badge status" data-project-status="${item.status.slug}">${item.status.name}</span>
			</div>

			<div class="bullet"></div>

			<div class="info">
				<div><i class="calendar icon"></i>${format_date( item.date )} ${format_time( item.date )}</div>
				<div><i class="user icon"></i>${item.user.name}</div>
			</div>
		`

		if ( item.status.slug === 'completed' ) {
			div.classList.add( 'completed' )

			const bullet = div.querySelector( '.bullet' )
			bullet.innerHTML = '<i class="small check icon"></i>'
		}

		return div
	}

	delete_remaining_work_confirmation( e ) {
		console.log( 'delete remaining work?', e.currentTarget )

		const confirmation = project.get_modal( 'delete-remaining-work-confirm' )

		confirmation.modal(
			{
				onApprove: () => {
					console.log( 'approved' )
					this.delete_remaining_work( e )
				}
			}
		)
		confirmation.modal( 'show' )
	}

	delete_remaining_work( e ) {
		console.log( 'delete remaining work', e.currentTarget )

		const form    = jQuery( e.currentTarget.closest( 'form' ) )
		const post_id = Number( form.find( '[name="post-id"]' ).val() )

		const { nonce, ajax_url } = gt_ajax_object

		const data = {
			action: 'delete_remaining_work',
			post_id,
			nonce
		}

		console.log( 'AJAX data', data )

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				dataType: 'json',
				data,
				cache: false
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				if ( response.error ) {
					app.show_toast_ajax_response( response.toast, 'error' )
					return
				}

				app.show_toast_ajax_response( response.toast )

				project.show_remaining_work_property_toast( response.has_remaining_work )

				project.set_remaining_work( response.project_remaining_work ?? [] )
				project.update_properties_meta( response.project_properties )
                app.close_modals()
				project.render_remaining_work()
			}
		)
	}

	init_modal_update_deadline() {
		console.log( 'init modal: update deadline' )
		const modal = this.get_modal( 'update-project-deadline' )

		const deadlines  = this.get_meta( 'project-deadlines' ).get_value()
		const reset_btn  = jQuery( 'button[data-action="reset-project-deadline"]' )
		const delete_btn = jQuery( 'button[data-action="delete-project-deadline"]' )
		const messages   = modal.find( '.ui.message:not(.error)' )
		const assembly   = this.get_meta_value( 'project-assembly' )
		const calendar   = jQuery( '#date-project-deadline-edit' )

		// resets.
		reset_btn.attr( 'data-hidden', 'true' )
		delete_btn.attr( 'data-hidden', 'true' )
		messages.addClass( 'hidden' )
		calendar.calendar( 'clear' )

		// sets.
		jQuery( '.project-post-id' ).val( project.get_meta( 'post-id' ).get_value() )

		if ( deadlines && action_tab in deadlines ) {
			const current    = deadlines[ action_tab ]
			const properties = project.get_meta_value( 'project-properties' )
			const estimates  = typeof properties === 'object' ? 'estimates-complete' in properties : false

			calendar.calendar( 'set date', current.date ?? '' )

			if ( current.meta ) {
				modal.find( '.ui.message[data-date-meta="locked"]' ).removeClass( 'hidden' )

				if ( estimates && assembly.date ) {
					console.debug( 'Berechnung möglich' )
					reset_btn.removeAttr( 'data-hidden' )
				} else {
					console.debug( 'Berechnung nicht möglich' )
					delete_btn.removeAttr( 'data-hidden' )
				}

			} else {
				modal.find( '.ui.message[data-date-meta="synced"]' ).removeClass( 'hidden' )
			}
		} else {
			modal.find( '.ui.message[data-date-meta="synced"]' ).removeClass( 'hidden' )
		}

		modal.modal( { autofocus: false } ).modal( 'show' )
	}

	update_deadline( e ) {
		console.log( 'update deadline', e.currentTarget )

		// enable AJAX.
		e.preventDefault()

		const form = jQuery( e.currentTarget )

		const form_buttons  = jQuery( '.form-actions button' )
		const submit_button = jQuery( '.submit', form )
		form_buttons.attr( 'disabled', 'disabled' )
		submit_button.addClass( 'loading elastic' )

		const { nonce, ajax_url } = gt_ajax_object

		const form_data = form.serialize()

		const data = {
			action: 'update_project_deadline',
			form_data,
			status: action_tab,
			nonce
		}

		console.log( data )

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				dataType: 'json',
				data,
				cache: false
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				form_buttons.removeAttr( 'disabled' )
				submit_button.removeClass( 'loading elastic' )

				if ( response.success === false ) {
					app.show_toast_ajax_response( response.data.toast, 'error' )

					if ( app.is_debug_enabled() ) {
						app.show_toast_ajax_response( response.data.error, 'error' )
					}
					return
				}

				app.show_toast_ajax_response( response.toast )

				update_meta_list_item( 'project-deadlines', response.project.deadlines )
				project.render_project_schedule()

                app.close_modals()
			}
		)
	}

	reset_deadline( e ) {
		console.log( 'reset deadline', e.currentTarget )

		// enable AJAX.
		e.preventDefault()

		const form = jQuery( '#form-project-deadline-edit' )

		const form_buttons = jQuery( '.form-actions button' )
		const reset_button = jQuery( 'button[data-action="reset-project-deadline"]', form )
		form_buttons.attr( 'disabled', 'disabled' )
		reset_button.addClass( 'loading' )

		const { nonce, post_id, ajax_url } = gt_ajax_object

		const data = {
			action: 'reset_project_deadline',
			post_id,
			status: action_tab,
			nonce
		}

		console.log( data, form )

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				dataType: 'json',
				data,
				cache: false
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				form_buttons.removeAttr( 'disabled' )
				reset_button.removeClass( 'loading' )

				if ( response.success === false ) {
					app.show_toast_ajax_response( response.data.toast, 'error' )

					if ( app.is_debug_enabled() ) {
						app.show_toast_ajax_response( response.data.error, 'error' )
					}
					return
				}

				app.show_toast_ajax_response( response.toast )

				update_meta_list_item( 'project-deadlines', response.project.deadlines )
				project.render_project_schedule()

                app.close_modals()
			}
		)
	}

	delete_deadline_confirmation( e ) {
		console.log( 'delete deadline?', e.currentTarget )

		const confirmation = project.get_modal( 'delete-project-deadline-confirm' )

		confirmation.modal(
			{
				onApprove: () => {
					console.log( 'approved' )
					this.delete_deadline( e )
				}
			}
		)
		confirmation.modal( 'show' )
	}

	delete_deadline( e ) {
		console.log( 'delete deadline', e.currentTarget )

		// enable AJAX.
		e.preventDefault()

		const form = jQuery( '#form-project-deadline-edit' )

		const form_buttons  = jQuery( '.form-actions button' )
		const delete_button = jQuery( 'button[data-action="delete-project-deadline"]', form )
		form_buttons.attr( 'disabled', 'disabled' )
		delete_button.addClass( 'loading elastic' )

		const { nonce, post_id, ajax_url } = gt_ajax_object

		const data = {
			action: 'delete_project_deadline',
			post_id,
			status: action_tab,
			nonce
		}

		console.log( data, form )

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				dataType: 'json',
				data,
				cache: false
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				form_buttons.removeAttr( 'disabled' )
				delete_button.removeClass( 'loading elastic' )

				if ( response.success === false ) {
					app.show_toast_ajax_response( response.data.toast, 'error' )

					if ( app.is_debug_enabled() ) {
						app.show_toast_ajax_response( response.data.error, 'error' )
					}
					return
				}

				app.show_toast_ajax_response( response.toast )

				update_meta_list_item( 'project-deadlines', response.project.deadlines )
				project.render_project_schedule()

                app.close_modals()
			}
		)
	}

	init_modal_update_storage_yard_assembly() {
		console.log( 'init modal: assembly storage yard' )

		const modal = this.get_modal( 'project-storage-yard-assembly' )
		const form     = jQuery( '#form-project-storage-yard-assembly' )
		const input    = jQuery( '#project-storage-yard-assembly' )

		// resets.
		form.form( 'reset' )

        const meta = this.get_meta_value( 'project-storage-yards' )

        if ( meta?.assembly ) {
            input.val( meta.assembly )
        }

		form.find( 'input[name="post-id"]' ).val( this.post_id )

		modal.modal( { autofocus: false } ).modal( 'show' )
	}

	init_modal_update_storage_yard() {
		console.log( 'init modal: update status worker', this.post_id )

		const modal    = jQuery( '[data-modal="project-storage-yard-edit"]' )
		const form     = jQuery( '#form-project-storage-yard-edit' )
		const status   = action_tab
		const meta     = this.get_meta_value( 'project-storage-yards' )
		const input    = jQuery( '#project-storage-yard-edit' )

		// resets.
		form.form( 'reset' )

		// sets.
		if ( meta?.[status] ) {
			input.val( meta[status] )
		}

		modal.find( 'input[name="project-status"' ).val( status )
		form.find( 'input[name="post-id"' ).val( this.post_id )

		modal.modal( { autofocus: false } ).modal( 'show' )
	}

	update_storage_yard( e ) {
		console.log( 'update storage yard', e )
		e.preventDefault()

		const form = jQuery( e.currentTarget )

		const form_buttons  = jQuery( '.form-actions button', form )
		const submit_button = jQuery( '.submit', form )
		form_buttons.attr( 'disabled', 'disabled' )
		submit_button.addClass( 'loading elastic' )

		const { nonce, ajax_url } = gt_ajax_object

		const data = {
			action: 'update_project_storage_yard',
			nonce,
			form_data: form.serialize()
		}

		console.log( data )

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				dataType: 'json',
				data,
				cache: false,
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				form_buttons.removeAttr( 'disabled' )
				submit_button.removeClass( 'loading elastic' )

				if ( response.success === false ) {
					app.show_toast_ajax_response( response.data.toast, 'error' )

					if ( app.is_debug_enabled() ) {
						app.show_toast_ajax_response( response.data.error, 'error' )
					}
					return
				}

				app.show_toast_ajax_response( response.toast )

				update_meta_list_item( 'project-storage-yards', response.project.storage_yards )

				const modal = jQuery( form.closest( '.ui.modal' ) )
				modal.modal( 'hide' )
			}
		)
	}

	update_storage_yard_assembly( e ) {
		console.log( 'update storage yard', e )
		e.preventDefault()

		const form = jQuery( e.currentTarget )

		const form_buttons  = jQuery( '.form-actions button', form )
		const submit_button = jQuery( '.submit', form )
		form_buttons.attr( 'disabled', 'disabled' )
		submit_button.addClass( 'loading elastic' )

		const { nonce, ajax_url } = gt_ajax_object

		const data = {
			action: 'update_project_storage_yard',
			nonce,
			form_data: form.serialize()
		}

		console.log( data )

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				dataType: 'json',
				data,
				cache: false,
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				form_buttons.removeAttr( 'disabled' )
				submit_button.removeClass( 'loading elastic' )

				if ( response.success === false ) {
					app.show_toast_ajax_response( response.data.toast, 'error' )

					if ( app.is_debug_enabled() ) {
						app.show_toast_ajax_response( response.data.error, 'error' )
					}
					return
				}

                update_meta_list_item( 'project-storage-yards', response.project.storage_yards )

                const modal = jQuery( form.closest( '.ui.modal' ) )
                modal.modal( 'hide' )

                void project.status_controller.api_approve_status_check()
			}
		)
	}

	render_approvals() {
		console.log( 'render approvals', action_tab )

		if ( project.get_meta_value( 'project-status' ).slug !== action_tab ) {
			this.disallow_approval()
			return
		}

		switch ( action_tab ) {
            case 'planning':
                if (
                    this.has_property( 'estimates-complete' ) &&
                    this.has_property( 'offer-estimates-complete' )
                ) {
                    this.allow_approval()
                } else {
                    this.disallow_approval()
                }

                break

			case 'cutting':
			case 'cnc':
			case 'fitting':
			case 'finishing':
				if ( this.has_property( 'in-progress' ) ) {
					this.allow_approval()
				} else {
					this.disallow_approval()
				}
				break

			case 'assembly':
				if ( ! ( this.has_property( 'paused' ) ) )  {
					this.allow_approval()
				} else {
					this.disallow_approval()
				}
				break

			default:
		}
	}



	check_project_status_in_progress() {
		const substatus = project.get_meta( 'project-substatus' ).get_value()

		console.log( 'substatus', substatus )

		if ( ! substatus ) {
			return false
		}

		return substatus.slug.includes( 'progress' )
	}

	allow_approval() {
		const container = document.querySelector( '.approval-container' )

		if ( ! container ) {
			return
		}

		// hide status-todo
		for ( const el of container.querySelectorAll( '.status-todo', container ) ) {
			el.dataset.hidden = 'true'
		}

		// show approval button
		for ( const el of container.querySelectorAll( 'button[data-approval-status]' ) ) {
			el.dataset.isAllowed = '1'
		}
	}

	disallow_approval() {
		const container = document.querySelector( '.approval-container' )

		if ( ! container ) {
			return
		}

		// hide status-todo
		for ( const el of container.querySelectorAll( '.status-todo', container ) ) {
			el.dataset.hidden = 'false'
		}

		// show approval button
		for ( const el of container.querySelectorAll( 'button[data-approval-status]' ) ) {
			el.dataset.isAllowed = '0'
		}
	}

	set_project_documents( data ) {
		console.debug( 'set project documents', data )

		this.project_documents = data
	}

	set_transfer_documents( data ) {
		console.debug( 'set transfer documents', data )

		this.transfer_documents = data
	}

	render_project_documents() {
		console.debug( 'render project_documents', this.project_documents.length )

		const container = document.querySelector( '.action-container[data-post-type="attachment"][data-category="project-document"]' )

		if ( ! container ) {
			return
		}

		const items    = this.project_documents
		const count_el = container.querySelector( '.count' )
		const list_el  = container.querySelector( '.item-list' )
		const actions  = container.querySelector( '.view-actions' )
		const count    = items.length

		console.debug( 'all project documents', this.project_documents )

		count_el.innerHTML = count.toString()
		list_el.innerHTML  = ''

		if ( count > 0 ) {
			for ( const item of items ) {
				const li = document.createElement( 'li' )

				li.classList.add( 'list-item', 'attachment-item', 'collapsed' )
				li.dataset.category = 'project-document'
				li.dataset.postId   = item.post_id
				li.dataset.type     = item.type

				li.innerHTML = `
					<div class="item-title">	
						<div class="title"><i class="file icon"></i> ${item.title}</div>
					
						<div class="details">
							<span>
								<i class="file icon"></i> ${item.extension.ext.toUpperCase()}
							</span>
	
							<span>
								<i class="download icon"></i> ${item.filesize.human}
							</span>
	
							<span>
								<i class="calendar alternate outline icon"></i> ${format_date( item.date )}
							</span>
						</div>
						
					</div>
					
					<div class="item-info">
						<a href="${item.permalink}" class="ui basic blue icon button" target="_blank">
							<i class="download icon"></i>
						</a>
					</div>
					
					<div class="item-actions">
						<a class="ui tiny primary button debug" href="/wp-admin/post.php?post=${item.post_id}&action=edit" 
						target="_blank" data-debug data-hidden="${( gt_user_settings.enable_debug === '1' ? 'false' : 'true' )}">Post bearbeiten</a>

						<button class="ui small compact button" data-action="update-project-document">
							bearbeiten
						</button>
					</div>
				`

				const icon = li.querySelector( '.title .icon' )

				switch ( item.type ) {
					case 'application/pdf':
						icon.classList.add( 'pdf', 'outline' )
						break
					case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
						icon.classList.add( 'word', 'outline' )
						break
					case 'image/webp':
					case 'image/jpeg':
					case 'image/png':
						icon.classList.add( 'image', 'outline' )
						break
					default:
						icon.classList.add( 'alternate', 'outline' )
						break
				}

				list_el.append( li )
			}
		}
	}

	init_modal_update_project_document( item ) {
		const post_id = Number( item.dataset.postId )
		const post    = this.project_documents.find( el => el.post_id === post_id )
		console.debug( 'update project document', post_id, post )

		const modal = this.get_modal( 'edit-project-document' )
		const title = jQuery( '#project-document-title-edit' )

		const form = jQuery( '#form-edit-project-document' )

		// resets.
		form.form( 'reset' )

		// sets.
		title.val( post.title )
		modal.find( '[name="post-id"]' ).val( post_id )

		modal.modal( { autofocus: false } ).modal( 'show' )
	}

	update_project_document( e ) {
		console.debug( 'update project document', e.currentTarget )
		e.preventDefault()

		const form      = jQuery( e.currentTarget )
		const form_data = form.serialize()

		const form_buttons  = jQuery( '.form-actions button', form )
		const submit_button = jQuery( '.submit', form )
		form_buttons.attr( 'disabled', 'disabled' )
		submit_button.addClass( 'loading elastic' )

		const { nonce, post_id, ajax_url } = gt_ajax_object

		const data = {
			action: 'update_project_document',
			post_id,
			form_data,
			nonce
		}

		console.debug( 'AJAX data', data )

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				dataType: 'json',
				data,
				cache: false
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				form_buttons.removeAttr( 'disabled' )
				submit_button.removeClass( 'loading elastic' )

				if ( response.success === false ) {
					app.show_toast_ajax_response( response.data.toast, 'error' )

					if ( app.is_debug_enabled() ) {
						app.show_toast_ajax_response( response.data.error, 'error' )
					}
					return
				}

				if ( response.error ) {
					app.show_toast_ajax_response( response.toast, 'error' )
					return
				}

				app.show_toast_ajax_response( response.toast )

				const index = project.project_documents.findIndex( el => el.post_id === Number( response.attachment?.ID ) )
				project.project_documents[index].title = response.attachment?.post_title

                app.close_modals()
				project.render_project_documents()
			}
		)
	}

	init_modal_add_project_documents() {
		console.log( 'init modal: add project documents', this.post_id )

		const modal  = jQuery( '[data-modal="add-project-documents"]' )
		const form   = jQuery( 'form', modal )

		// resets.
		form.form( 'reset' )
		this.clear_add_project_documents_list()
		app.create_add_project_document_input()

		modal.modal( { autofocus: false } ).modal( 'show' )
	}

	clear_add_project_documents_list() {
		const container = document.querySelector( '[data-modal="add-project-documents"] .files-list' )
		container.innerHTML = ''
	}

	add_project_documents( e ) {
		console.log( 'add project documents', e )

		// enable AJAX.
		e.preventDefault()

		const form = jQuery( e.currentTarget )

		const submit = jQuery( '.submit', form )
		submit.attr( 'disabled', 'disabled' )
		submit.addClass( 'loading' )

		const { ajax_url, post_id, nonce } = gt_ajax_object

		const data = new FormData( form[0] )
		data.append( 'action', 'add_project_documents' )
		data.append( 'post_id', post_id )
		data.append( 'nonce', nonce )

		console.debug( 'AJAX data', data )

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				data,
				cache: false,
				contentType: false,
				processData: false,
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				if ( response.error ) {
					app.show_toast_ajax_response( response.toast, 'error' )
					return
				}

				if ( response.count ) {
					app.show_toast_ajax_response( response.toast )
				} else {
					app.show_toast_ajax_response( response.toast, '', 'info' )
				}

				submit.removeAttr( 'disabled' )
				submit.removeClass( 'loading' )

				project.set_project_documents( response.attachments ?? [] )
                app.close_modals()
				project.render_project_documents()
			}
		);
	}

	delete_project_document_confirmation( e ) {
		console.log( 'delete project document?', this.post_id )

		const confirmation = this.get_modal( 'delete-project-document-confirm' )

		confirmation.modal(
			{
				onApprove: () => {
					console.log( 'approved' )
					this.delete_project_document( e )
				}
			}
		)
		confirmation.modal( 'show' )
	}

	delete_project_document( e ) {
		console.log( 'delete project document', e.currentTarget )

		const form          = jQuery( e.currentTarget.closest( 'form' ) )
		const attachment_id = form.find( '[name="post-id"]' ).val()

		const { nonce, post_id, ajax_url } = gt_ajax_object

		const data = {
			action: 'delete_project_document',
			post_id,
			attachment_id,
			nonce
		}

		console.log( 'AJAX data', data )

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				dataType: 'json',
				data,
				cache: false
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				if ( response.success === false ) {
					app.show_toast_ajax_response( response.data.toast, 'error' )

					if ( app.is_debug_enabled() ) {
						app.show_toast_ajax_response( response.data.error, 'error' )
					}
					return
				}

				app.show_toast_ajax_response( response.toast )

				project.set_project_documents( response.attachments ?? [] )
				project.render_project_documents()
                app.close_modals()
			}
		)
	}

	render_transfer_documents() {
		console.debug( 'render transfer files', this.transfer_documents.length )

		const container = document.querySelector( '.action-container[data-post-type="attachment"][data-category="transfer-document"]' )

		if ( ! container ) {
			return
		}

		const items    = this.transfer_documents
		const count_el = container.querySelector( '.count' )
		const list_el  = container.querySelector( '.item-list' )
		const count    = items.length

		console.debug( 'all transfer documents', this.transfer_documents )

		count_el.innerHTML = count.toString()
		list_el.innerHTML  = ''

		if ( count > 0 ) {
			for ( const item of items.toReversed() ) {
				const li = document.createElement( 'li' )

				li.classList.add( 'list-item', 'attachment-item', 'collapsed' )
				li.dataset.category = 'transfer-document'
				li.dataset.postId   = item.post_id
				li.dataset.type     = item.type

				li.innerHTML = `
					<div class="item-type">
						<i class="file icon"></i>
					</div>
					
					<div class="item-title">
						<a href="${item.permalink}" target="_blank">${item.title}</a>
					</div>
					
					<div class="item-extension">
						<i class="file icon"></i>  ${item.extension.ext.toUpperCase()}
					</div>
					
					<div class="item-filesize">
						<i class="download icon"></i> ${item.filesize.human}
					</div>
					
					<div class="item-date">
						<i class="calendar alternate outline icon"></i> ${format_date( item.date )}
					</div>
					
					<div class="
					actions">
						<button class="ui tiny icon button" data-action="update-transfer-document">bearbeiten</button>
					</div>
				`

				const icon = li.querySelector( '.item-type .icon' )

				switch ( item.type ) {
					case 'application/pdf':
						icon.classList.add( 'pdf', 'outline' )
						break
					case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
						icon.classList.add( 'word', 'outline' )
						break
					case 'image/webp':
					case 'image/jpeg':
					case 'image/png':
						icon.classList.add( 'image', 'outline' )
						break
					default:
						icon.classList.add( 'alternate', 'outline' )
						break
				}

				list_el.append( li )
			}
		}
	}


	init_modal_add_transfer_documents() {
		console.log( 'init modal: add transfer documents', this.post_id )

		const modal  = jQuery( '[data-modal="add-transfer-documents"]' )
		const form   = jQuery( 'form', modal )

		// resets.
		form.form( 'reset' )
		this.clear_add_transfer_documents_list()
		this.create_add_transfer_document_input()

		modal.modal( { autofocus: false } ).modal( 'show' )
	}

	clear_add_transfer_documents_list() {
		const container = document.querySelector( '[data-modal="add-transfer-documents"] .files-list' )
		container.innerHTML = ''
	}

	create_add_transfer_document_input() {
		console.log( 'add transfer document input' )

		const documents = document.querySelector( '.transfer-documents-list' )
		const id        = Date.now()
		const container = documents.querySelector( '.files-list' )

		const file = document.createElement( 'div' )

		file.classList.add( 'inline', 'fields', 'transfer-document-fields' )
		file.innerHTML = `
		<div class="six wide field">
			<div class="ui labeled input">
				<label for="transfer-document-titles[${id}]" class="ui label"></label>
				<input type="text" class="form-control" name="transfer-document-titles[${id}]" id="transfer-document-titles[${id}]" placeholder="Name eingeben... (optional)"/>
			</div>
		</div>

		<div class="ten wide field">
			<div class="ui file input">
				<input type="file" name="transfer-document-${id}" id="transfer-document-${id}">
			</div>

			<button type="button" class="ui red icon button" data-action="remove-transfer-document-input">
				<i class="times icon"></i>
			</button>
		</div>
	`
		container.append( file )

		// Form Validation Fix für neue DOM Inputs
		jQuery( '#form-add-transfer-documents' ).form( 'set optional', `#transfer-document-titles[${id}]`, true )
	}

	delete_transfer_document_input( target ) {
		console.log( 'delete transfer file input', target )

		const file = target.closest( '.transfer-document-fields' )
		file.remove()
	}

	add_transfer_documents( e ) {
		console.log( 'add transfer documents', e )

		// enable AJAX.
		e.preventDefault()

		const form = jQuery( e.currentTarget )

		const submit = jQuery( '.submit', form )
		submit.attr( 'disabled', 'disabled' )
		submit.addClass( 'loading' )

		const { ajax_url, post_id, nonce } = gt_ajax_object

		const data = new FormData( form[0] )
		data.append( 'action', 'add_transfer_documents' )
		data.append( 'post_id', post_id )
		data.append( 'nonce', nonce )

		console.debug( 'AJAX data', data )

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				data,
				cache: false,
				contentType: false,
				processData: false,
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				if ( response.error ) {
					app.show_toast_ajax_response( response.toast, 'error' )
					return
				}

				if ( response.count ) {
					app.show_toast_ajax_response( response.toast )
				} else {
					app.show_toast_ajax_response( response.toast, '', 'info' )
				}

				submit.removeAttr( 'disabled' )
				submit.removeClass( 'loading' )

				project.set_transfer_documents( response.attachments ?? [] )
                app.close_modals()
				project.render_transfer_documents()
			}
		);
	}

	init_modal_update_transfer_document( item ) {
		const post_id = Number( item.dataset.postId )
		const post    = this.transfer_documents.find( el => el.post_id === post_id )
		console.debug( 'update transfer document', post_id, post )

		const modal = this.get_modal( 'edit-transfer-document' )
		const title = jQuery( '#transfer-document-title-edit' )

		const form = jQuery( '#form-edit-transfer-document' )

		// resets.
		form.form( 'reset' )

		// sets.
		title.val( post.title )
		modal.find( '[name="post-id"]' ).val( post_id )

		modal.modal( { autofocus: false } ).modal( 'show' )
	}

	update_transfer_document( e ) {
		console.debug( 'update transfer document', e.currentTarget )
		e.preventDefault()

		const form      = jQuery( e.currentTarget )
		const form_data = form.serialize()

		const form_buttons  = jQuery( '.form-actions button', form )
		const submit_button = jQuery( '.submit', form )
		form_buttons.attr( 'disabled', 'disabled' )
		submit_button.addClass( 'loading elastic' )

		const { nonce, post_id, ajax_url } = gt_ajax_object

		const data = {
			action: 'update_transfer_document',
			post_id,
			form_data,
			nonce
		}

		console.debug( 'AJAX data', data )

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				dataType: 'json',
				data,
				cache: false
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				form_buttons.removeAttr( 'disabled' )
				submit_button.removeClass( 'loading elastic' )

				if ( response.success === false ) {
					app.show_toast_ajax_response( response.data.toast, 'error' )

					if ( app.is_debug_enabled() ) {
						app.show_toast_ajax_response( response.data.error, 'error' )
					}
					return
				}

				if ( response.error ) {
					app.show_toast_ajax_response( response.toast, 'error' )
					return
				}

				app.show_toast_ajax_response( response.toast )

				const index = project.transfer_documents.findIndex( el => el.post_id === Number( response.attachment?.ID ) )
				project.transfer_documents[index].title = response.attachment?.post_title

                app.close_modals()
				project.render_transfer_documents()
			}
		)
	}

	delete_transfer_document_confirmation( e ) {
		console.log( 'delete transfer document?', this.post_id )

		const confirmation = this.get_modal( 'delete-transfer-document-confirm' )

		confirmation.modal(
			{
				onApprove: () => {
					console.log( 'approved' )
					this.delete_transfer_document( e )
				}
			}
		)
		confirmation.modal( 'show' )
	}

	delete_transfer_document( e ) {
		console.log( 'delete transfer document', e.currentTarget )

		const form          = jQuery( e.currentTarget.closest( 'form' ) )
		const attachment_id = form.find( '[name="post-id"]' ).val()

		const { nonce, post_id, ajax_url } = gt_ajax_object

		const data = {
			action: 'delete_transfer_document',
			post_id,
			attachment_id,
			nonce
		}

		console.log( 'AJAX data', data )

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				dataType: 'json',
				data,
				cache: false
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				if ( response.success === false ) {
					app.show_toast_ajax_response( response.data.toast, 'error' )

					if ( app.is_debug_enabled() ) {
						app.show_toast_ajax_response( response.data.error, 'error' )
					}
					return
				}

				app.show_toast_ajax_response( response.toast )

				project.set_transfer_documents( response.attachments ?? [] )
				project.render_transfer_documents()
                app.close_modals()
			}
		)
	}

	render_status_schedule() {
		console.log( 'render status schedule', action_tab )

		const container = document.querySelector( '.project-schedule-container' )

		if ( ! container ) {
			return
		}

		const labels  = container.querySelectorAll( '.label[data-status]' )
		const info    = container.querySelector( '.label:not([data-status])' )
		const good    = container.querySelector( '.label[data-status="good"]' )
		const ok      = container.querySelector( '.label[data-status="ok"]' )
		const bad     = container.querySelector( '.label[data-status="bad"]' )
		const tooltip = container.querySelector( '.tooltip' )
		const status  = action_tab

		const deadlines    = project.get_meta_value( 'project-deadlines' ) ?? {}
		const deadline_sql = deadlines?.[status] ?? null

		if ( ! deadline_sql ) {
			info.classList.add( 'active' )
			info.dataset.hidden = 'false'
			tooltip.innerHTML = 'Es gibt keine Daten, wann dieser Bereich abgeschlossen sein soll.'
			return
		}

		info.dataset.hidden = 'true'

		for ( const label of labels ) {
			label.innerHTML = ''
			label.classList.remove( 'active' )
		}

		const deadline  = DateTime.fromSQL( deadline_sql.date )
		const today     = DateTime.now().startOf( 'day' );
		const approvals = project.get_meta_value( 'project-approval-dates' )

		if ( approvals && status in approvals ) {
			const approved      = approvals[status]
			const approved_date = DateTime.fromSQL( approved.date )

			if ( approved_date.hasSame( deadline, 'day' ) ) {
				ok.classList.add( 'active' )

				ok.innerHTML      = '<i class="check icon"></i>'
				tooltip.innerHTML = '<p>Der Bereich wurde am erforderlichen Tag abgeschlossen.</p>'
			} else if ( deadline < approved_date ) {
				bad.classList.add( 'active' )

				bad.innerHTML     = '<i class="check icon"></i>'
				tooltip.innerHTML = '<p>Der Bereich wurde verspätet abgeschlossen.</p>'
			} else {
				good.classList.add( 'active' )

				good.innerHTML    = '<i class="check icon"></i>'
				tooltip.innerHTML = '<p>Der Bereich wurde rechtzeitig abgeschlossen.</p>'
			}

			tooltip.innerHTML += `
				<p>Fertigstellung bis: <span class="badge date"><i class="calendar alternate outline icon"></i>${format_date( deadline_sql.date )}</span></p>
				<p>Fertigstellung am: <span class="badge date"><i class="calendar alternate outline icon"></i>${format_date( approved.date )}</span></p>
			`
		} else {
			if ( today.hasSame( deadline, 'day', false ) ) {
				ok.classList.add( 'active' )

				tooltip.innerHTML = '<p>Der Bereich soll heute abgeschlossen werden.</p>'
			} else if ( deadline < today ) {
				bad.classList.add( 'active' )

				tooltip.innerHTML = '<p>Der Bereich sollte bereits abgeschlossen sein.</p>'
			} else {
				good.classList.add( 'active' )

				tooltip.innerHTML = '<p>Es ist noch genug Zeit, diesen Bereich abzuschließen.</p>'
			}

			tooltip.innerHTML += `
				<p>Fertigstellung bis: <span class="badge date"><i class="calendar alternate outline icon"></i>${format_date( deadline_sql.date )}</span></p>
			`
		}
	}

	render_project_schedule() {
		if ( ! [ 'data', 'debug' ].includes( action_tab ) ) {
			this.render_status_schedule()
			return
		}

		console.log( 'render project schedule' )

		const container = document.querySelector( '.project-schedule-container' )

		if ( ! container ) {
			return
		}

		const labels  = container.querySelectorAll( '.label[data-status]' )
		const info    = container.querySelector( '.label:not([data-status])' )
		const good    = container.querySelector( '.label[data-status="good"]' )
		const ok      = container.querySelector( '.label[data-status="ok"]' )
		const bad     = container.querySelector( '.label[data-status="bad"]' )
		const tooltip = container.querySelector( '.tooltip' )
		let status  = project.get_meta_value( 'project-status' ).slug

		const deadlines = project.get_meta_value( 'project-deadlines' ) ?? {}

		if ( status === 'completed' ) {
			status = 'assembly'
		}

		const deadline_sql = deadlines?.[status] ?? null

		if ( ! deadline_sql ) {
			info.classList.add( 'active' )
			info.dataset.hidden = 'false'
			tooltip.innerHTML = 'Es gibt keine Daten, wann der aktuelle Bereich abgeschlossen sein soll.'
			return
		}

		info.dataset.hidden = 'true'

		for ( const label of labels ) {
			label.innerHTML = ''
			label.classList.remove( 'active' )
		}

		const deadline  = DateTime.fromSQL( deadline_sql.date )
		const today     = DateTime.now().startOf( 'day' );
		const approvals = project.get_meta_value( 'project-approval-dates' )

		if ( approvals && status in approvals ) {
			const approved      = approvals[status]
			const approved_date = DateTime.fromSQL( approved.date )

			console.log( 'deadline', deadline, 'approved', approved, approved_date )

			if ( approved_date.hasSame( deadline, 'day' ) ) {
				ok.classList.add( 'active' )

				ok.innerHTML      = '<i class="check icon"></i>'
				tooltip.innerHTML = '<p>Das Projekt wurde am erforderlichen Tag abgeschlossen.</p>'
			} else if ( deadline < approved_date ) {
				bad.classList.add( 'active' )

				bad.innerHTML     = '<i class="check icon"></i>'
				tooltip.innerHTML = '<p>Das Projekt wurde verspätet abgeschlossen.</p>'
			} else {
				good.classList.add( 'active' )

				good.innerHTML    = '<i class="check icon"></i>'
				tooltip.innerHTML = '<p>Das Projekt wurde rechtzeitig abgeschlossen.</p>'
			}

			tooltip.innerHTML += `
				<p>Fertigstellung bis: <span class="badge date"><i class="calendar alternate outline icon"></i>${format_date( deadline_sql.date )}</span></p>
				<p>Fertigstellung am: <span class="badge date"><i class="calendar alternate outline icon"></i>${format_date( approved.date )}</span></p>
			`
		} else {
			if ( today.hasSame( deadline, 'day', false ) ) {
				ok.classList.add( 'active' )

				tooltip.innerHTML = '<p>Der aktuelle Bereich soll heute abgeschlossen werden.</p>'
			} else if ( deadline < today ) {
				bad.classList.add( 'active' )

				tooltip.innerHTML = '<p>Der aktuelle Bereich sollte bereits abgeschlossen sein.</p>'
			} else {
				good.classList.add( 'active' )

				tooltip.innerHTML = '<p>Es ist noch genug Zeit, den aktuellen Bereich abzuschließen.</p>'
			}

			tooltip.innerHTML += `
				<p>Fertigstellung bis: <span class="badge date"><i class="calendar alternate outline icon"></i>${format_date( deadline_sql.date )}</span></p>
			`
		}
	}



	set_times( data ) {
		console.debug( 'set times', data )

		this.times = data
	}

	set_user_notes( data ) {
		console.debug( 'set user notes', data )

		this.user_notes = data
	}

	render_user_notes() {
		console.log( 'render user notes' )

		const meta_container = document.querySelector( '.meta-item[data-project-meta="project-user-notes"]' )

		if ( meta_container ) {
			const description = meta_container.querySelector( '.meta-item__description' )
			description.innerHTML = this.user_notes !== '' ? this.user_notes : '<i class="blue edit icon"></i>'
		}

		const main_container = document.querySelector( '.project-notes[data-type="user"]' )

		if ( main_container ) {
			const note = main_container.querySelector( '.note' )
			note.innerHTML = this.user_notes
		}
	}

	init_modal_update_project_user_notes() {
		console.log( 'init modal: update user notes', this.post_id )

		const modal  = jQuery( '[data-modal="edit-project-user-notes"]' )
		const form   = jQuery( 'form', modal )
		const content = jQuery( '#project-user-notes-edit' )

		// resets.
		form.form( 'reset' )

		// sets
		content.val( this.user_notes )
		modal.find( '[name="post-id"]' ).val( this.post_id )

		modal.modal( { autofocus: false } ).modal( 'show' )
	}

	update_project_user_notes( e ) {
		console.debug( 'update user notes', e.currentTarget )
		e.preventDefault()

		const form      = jQuery( e.currentTarget )
		const form_data = form.serialize()

		const form_buttons  = jQuery( '.form-actions button', form )
		const submit_button = jQuery( '.submit', form )
		form_buttons.attr( 'disabled', 'disabled' )
		submit_button.addClass( 'loading elastic' )

		const { nonce, ajax_url } = gt_ajax_object

		const data = {
			action: 'update_project_user_notes',
			form_data,
			nonce
		}

		console.debug( 'AJAX data', data )

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				dataType: 'json',
				data,
				cache: false
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				form_buttons.removeAttr( 'disabled' )
				submit_button.removeClass( 'loading elastic' )

				if ( response.success === false ) {
					app.show_toast_ajax_response( response.data.toast, 'error' )

					if ( app.is_debug_enabled() ) {
						app.show_toast_ajax_response( response.data.error, 'error' )
					}
					return
				}

				if ( response.error ) {
					app.show_toast_ajax_response( response.toast, 'error' )
					return
				}

				app.show_toast_ajax_response( response.toast )

				project.set_user_notes( response.notes )
				project.render_user_notes()
                app.close_modals()
			}
		)
	}

	render_temp_approvals() {
		const container = document.querySelector( '.temp-approvals' )

		for ( const approval of container.querySelectorAll( '.approval' ) ) {
			approval.classList.remove( 'disabled', 'approved', 'active' )
		}

		const cnc       = container.querySelector( '.approval[data-status="cnc"]' )
		const finishing = container.querySelector( '.approval[data-status="finishing"]' )
		const fitting   = container.querySelector( '.approval[data-status="fitting"]' )

		if ( 'cutting' in project.get_meta_value( 'project-approval-dates' ) ) {
			cnc.classList.add( 'disabled' )
		} else {
			const progress = document.querySelector( '.project-progress__item[data-project-status="cnc"]' )

			if ( this.has_property( 'cnc-allowed' ) ) {
				cnc.classList.add( 'approved' )
				progress.setAttribute( 'data-current-status', '' )
			} else {
				progress.removeAttribute( 'data-current-status', '' )
			}
		}

		if ( 'cnc' in project.get_meta_value( 'project-approval-dates' ) ) {
			fitting.classList.add( 'disabled' )
		} else {
			const progress = document.querySelector( '.project-progress__item[data-project-status="fitting"]' )

			if ( this.has_property( 'fitting-allowed' ) ) {
				fitting.classList.add( 'approved' )
				progress.setAttribute( 'data-current-status', '' )
			} else {
				progress.removeAttribute( 'data-current-status', '' )
			}
		}

		if ( 'fitting' in project.get_meta_value( 'project-approval-dates' ) ) {
			finishing.classList.add( 'disabled' )
		} else {
			const progress = document.querySelector( '.project-progress__item[data-project-status="finishing"]' )

			if ( this.has_property( 'finishing-allowed' ) ) {
				finishing.classList.add( 'approved' )
				progress.setAttribute( 'data-current-status', '' )
			} else {
				progress.removeAttribute( 'data-current-status', '' )
			}
		}
	}

	render_dates() {
        this.render_meta( 'project-assembly' )
    }

	toggle_approval( e ) {
		console.log( 'toggle approval', e.currentTarget )

		const approval = e.currentTarget.parentElement
		const status = approval.dataset.status
		const approved = approval.classList.contains( 'approved' )

		console.log( approval, status, approved )

		if ( approved ) {
			const confirmation = this.get_modal( 'remove-approval-confirmation' )

			confirmation.modal(
				{
					onApprove: () => {
						console.log( 'approved' )
						this.remove_approval( status )
					}
				}
			)
			confirmation.modal( 'show' )
		} else {
			const confirmation = this.get_modal( 'add-approval-confirmation' )

			confirmation.modal(
				{
					onApprove: () => {
						console.log( 'approved' )
						this.add_approval( status )
					}
				}
			)
			confirmation.modal( 'show' )
		}
	}

	init_remove_approval() {
		const confirmation = this.get_modal( 'remove-approval-confirmation' )
		const status = action_tab

		confirmation.modal(
			{
				onApprove: () => {
					console.log( 'approved' )
					this.remove_approval( status )
				}
			}
		)
		confirmation.modal( 'show' )
	}

	add_approval( status ) {
		console.log( 'add approval', status )

		const { nonce, post_id, ajax_url } = gt_ajax_object

		const data = {
			action: 'add_approval',
			post_id,
			status,
			nonce
		}

		console.log( 'AJAX data', data )

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				dataType: 'json',
				data,
				cache: false
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				if ( response.success === false ) {
					app.show_toast_ajax_response( response.data.toast, 'error' )

					if ( app.is_debug_enabled() ) {
						app.show_toast_ajax_response( response.data.error, 'error' )
					}
					return
				}

				if ( response.error ) {
					app.show_toast_ajax_response( response.toast, 'error' )
					return
				}

				app.show_toast_ajax_response( response.toast )

				project.update_properties_meta( response.properties )
				project.render_temp_approvals()
                void project.times_controller.api_load_times()
			}
		)
	}

	remove_approval( status ) {
		console.log( 'remove approval', status )

		const { nonce, post_id, ajax_url } = gt_ajax_object

		const data = {
			action: 'remove_approval',
			post_id,
			status,
			action_tab,
			nonce
		}

		console.log( 'AJAX data', data )

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				dataType: 'json',
				data,
				cache: false
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				if ( response.redirect ) {
					const dimmer = jQuery( '.full-screen-loading-dimmer .dimmer' )
					dimmer.dimmer( 'show' )

					window.location.href = response.redirect
					return
				}

				if ( response.success === false ) {
					app.show_toast_ajax_response( response.data.toast, 'error' )

					if ( app.is_debug_enabled() ) {
						app.show_toast_ajax_response( response.data.error, 'error' )
					}
					return
				}

				if ( response.error ) {
					app.show_toast_ajax_response( response.toast, 'error' )
					return
				}

				app.show_toast_ajax_response( response.toast )

				project.update_properties_meta( response.properties )
				project.render_temp_approvals()
                void project.times_controller.api_load_times()
			}
		)
	}

	debug_event( e ) {
		e.preventDefault()

		console.log( 'debug', e.currentTarget, e.target, e )
	}

    init_modal_project_data() {
        const modal = app.get_modal( 'project-data' )

        const meta = project.get_meta( 'project-data' )
        meta.render()

        modal.modal( 'show' )
    }

    init_modal_project_data_update() {
        const modal = app.get_modal( 'update-project-data' )

        const meta = project.get_meta( 'project-data' )
        meta.render_form()

        modal.modal( 'show' )
    }

    update_project_data( e ) {
        console.debug( 'update project data', e.currentTarget )
        e.preventDefault()

        const form      = jQuery( e.currentTarget )
        const form_data = form.serialize()

        const form_buttons  = jQuery( '.form-actions button', form )
        const submit_button = jQuery( '.submit', form )
        form_buttons.attr( 'disabled', 'disabled' )
        submit_button.addClass( 'loading elastic' )

        const { nonce, post_id, ajax_url } = gt_ajax_object

        const data = {
            action: 'update_project_data',
            post_id,
            form_data,
            nonce
        }

        console.debug( 'AJAX data', data )

        jQuery.ajax(
            {
                url: ajax_url,
                type: 'POST',
                dataType: 'json',
                data,
                cache: false
            }
        ).done(
            ( response ) => {
                console.log( 'AJAX response', response )

                form_buttons.removeAttr( 'disabled' )
                submit_button.removeClass( 'loading elastic' )

                if ( response.success === false ) {
                    app.show_toast_ajax_response( response.data.toast, 'error' )

                    if ( app.is_debug_enabled() ) {
                        app.show_toast_ajax_response( response.data.error, 'error' )
                    }
                    return
                }

                if ( response.error ) {
                    app.show_toast_ajax_response( response.toast, 'error' )
                    return
                }

                app.show_toast_ajax_response( response.toast )

                project.set_meta( 'project-data', response.project.data )

                app.close_modals()
            }
        )
    }
}
